/**
 * Elite Batch 16 Part 4 — Workflow Templates 1508–1527
 * Sentiment analysis bots, survey data analyzers, viral content finders, web scraping
 * pipelines, ABM engagement nurtures, account handoffs, account plan generators,
 * Apollo lead enrichment, at-risk QBR invites, beta feedback, beta signup onboarding,
 * blog subscriber nurture, buying committee mappers, buying committee sync,
 * buying committee nurture, CI/CD alerts, CI/CD failure alerts, CRM data hygiene,
 * CRM duplicate detectors, and CRM lead sync via webhooks.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 16 PART 4 — TEMPLATES 1508–1527
// =============================================================================

export const ELITE_BATCH16_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1508. SENTIMENT ANALYSIS BOT (7 nodes)
  // ===================================================================

  "Sentiment Analysis Bot": {
    summary:
      "Receives text data via webhook, extracts the input text with AI, preprocesses and normalizes it in code, runs AI-powered sentiment analysis with confidence scoring, extracts granular aspect-level sentiments in code, generates visualization-ready chart data in code, and delivers the full results report to Slack.",
    nodes: [
      trigger("Webhook: Text submitted for sentiment analysis", "webhook_app", {
        source: "sentiment_pipeline",
        event: "text.submitted",
        howItStarts:
          "Fires when text content is submitted for sentiment analysis — e.g., customer review, support ticket, social media post, or survey response",
        setup:
          "Configure webhook endpoint in your application or integration platform. Set up OpenAI API key for sentiment analysis. Configure Slack webhook for results delivery.",
        inputFields: [],
      }),

      // 2. AI extract text
      aiNode(
        "2",
        "AI: Extract text for analysis",
        280,
        "You are a text extraction specialist. Extract the text content to be analyzed from the raw webhook payload regardless of format. Find the text body, the source type (review, ticket, social post, etc.), any author identifier, the language, and any associated metadata like product name or category. Respond JSON: {text_body: string, source_type: string, author_id: string, language: string, product_name: string, category: string, character_count: number, word_count: number}",
        "Raw text submission payload: {webhook_payload}",
        [
          { field: "text_body", as: "text_body", type: "string" },
          { field: "source_type", as: "source_type", type: "string" },
          { field: "author_id", as: "author_id", type: "string" },
          { field: "language", as: "language", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "character_count", as: "character_count", type: "number" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 3. Text preprocessor runCode
      n("3", "runCode", "Preprocess and normalize text", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize text for consistent sentiment analysis
const raw = vars.text_body || "";
const cleaned = raw.replace(/https?:\\/\\/\\S+/g, "").replace(/[\\r\\n]+/g, " ").replace(/\\s{2,}/g, " ").trim();
const lowered = cleaned.toLowerCase();
const wordCount = parseInt(vars.word_count || "0", 10) || cleaned.split(/\\s+/).filter(Boolean).length;
const sentences = cleaned.split(/[.!?]+/).filter(s => s.trim().length > 0);
const sentenceCount = sentences.length;
const avgWordsPerSentence = sentenceCount > 0 ? Math.round(wordCount / sentenceCount) : 0;
const hasEmoji = /[\\u{1F600}-\\u{1F64F}\\u{1F300}-\\u{1F5FF}\\u{1F680}-\\u{1F6FF}]/u.test(raw);
const hasExclamation = raw.includes("!");
const hasQuestion = raw.includes("?");
const capsRatio = raw.length > 0 ? Math.round((raw.replace(/[^A-Z]/g, "").length / raw.length) * 100) : 0;
return {
  cleaned_text: cleaned,
  normalized_text: lowered,
  processed_word_count: wordCount,
  sentence_count: sentenceCount,
  avg_words_per_sentence: avgWordsPerSentence,
  has_emoji: hasEmoji,
  has_exclamation: hasExclamation,
  has_question: hasQuestion,
  caps_ratio: capsRatio,
  text_ready: cleaned.length > 0,
};`,
          outputs: [
            { name: "cleaned_text", type: "string" },
            { name: "normalized_text", type: "string" },
            { name: "processed_word_count", type: "number" },
            { name: "sentence_count", type: "number" },
            { name: "avg_words_per_sentence", type: "number" },
            { name: "has_emoji", type: "boolean" },
            { name: "has_exclamation", type: "boolean" },
            { name: "has_question", type: "boolean" },
            { name: "caps_ratio", type: "number" },
            { name: "text_ready", type: "boolean" },
          ],
        },
      }),

      // 4. AI sentiment engine
      aiNode(
        "4",
        "AI: Analyze sentiment with confidence scoring",
        760,
        "You are an expert sentiment analysis engine. Analyze the provided text for overall sentiment (positive, negative, neutral, mixed), provide a confidence score 0-100, identify the emotional tone (angry, happy, frustrated, grateful, confused, etc.), detect urgency level (low, medium, high, critical), and identify key sentiment-driving phrases. Respond JSON: {overall_sentiment: string, sentiment_score: number, confidence: number, emotional_tone: string, urgency_level: string, key_positive_phrases: string, key_negative_phrases: string, sentiment_drivers: string, intent: string, recommendation: string}",
        "Text to analyze: {cleaned_text}. Source type: {source_type}. Language: {language}. Word count: {processed_word_count}. Sentences: {sentence_count}. Has emoji: {has_emoji}. Caps ratio: {caps_ratio}%. Context — Product: {product_name}, Category: {category}.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "confidence", as: "sentiment_confidence", type: "number" },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "key_positive_phrases",
            as: "key_positive_phrases",
            type: "string",
          },
          {
            field: "key_negative_phrases",
            as: "key_negative_phrases",
            type: "string",
          },
          {
            field: "sentiment_drivers",
            as: "sentiment_drivers",
            type: "string",
          },
          { field: "intent", as: "detected_intent", type: "string" },
          {
            field: "recommendation",
            as: "action_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Aspect extractor runCode
      n("5", "runCode", "Extract aspect-level sentiments", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract aspect-level sentiment breakdown
const positivePhrases = (vars.key_positive_phrases || "").split(",").map(p => p.trim()).filter(Boolean);
const negativePhrases = (vars.key_negative_phrases || "").split(",").map(p => p.trim()).filter(Boolean);
const sentimentScore = parseInt(vars.sentiment_score || "50", 10);
const aspects = [];
if (vars.product_name) aspects.push({ aspect: "Product: " + vars.product_name, polarity: sentimentScore >= 60 ? "positive" : sentimentScore <= 40 ? "negative" : "neutral" });
if (positivePhrases.length > 0) aspects.push({ aspect: "Strengths", polarity: "positive", phrases: positivePhrases.join("; ") });
if (negativePhrases.length > 0) aspects.push({ aspect: "Pain Points", polarity: "negative", phrases: negativePhrases.join("; ") });
const positiveCount = positivePhrases.length;
const negativeCount = negativePhrases.length;
const netSentimentRatio = (positiveCount + negativeCount) > 0 ? Math.round((positiveCount / (positiveCount + negativeCount)) * 100) : 50;
const aspectSummary = aspects.map(a => a.aspect + " (" + a.polarity + ")").join(" | ");
return {
  aspect_summary: aspectSummary,
  positive_phrase_count: positiveCount,
  negative_phrase_count: negativeCount,
  net_sentiment_ratio: netSentimentRatio,
  aspect_count: aspects.length,
  dominant_aspect: aspects.length > 0 ? aspects[0].aspect : "General",
};`,
          outputs: [
            { name: "aspect_summary", type: "string" },
            { name: "positive_phrase_count", type: "number" },
            { name: "negative_phrase_count", type: "number" },
            { name: "net_sentiment_ratio", type: "number" },
            { name: "aspect_count", type: "number" },
            { name: "dominant_aspect", type: "string" },
          ],
        },
      }),

      // 6. Visualization generator runCode
      n("6", "runCode", "Generate visualization data", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate chart-ready visualization data for sentiment report
const score = parseInt(vars.sentiment_score || "50", 10);
const confidence = parseInt(vars.sentiment_confidence || "0", 10);
const posCount = parseInt(vars.positive_phrase_count || "0", 10);
const negCount = parseInt(vars.negative_phrase_count || "0", 10);
const ratio = parseInt(vars.net_sentiment_ratio || "50", 10);
const gauge = score >= 80 ? "Excellent" : score >= 60 ? "Positive" : score >= 40 ? "Neutral" : score >= 20 ? "Negative" : "Critical";
const barChart = "Positive: " + "=".repeat(Math.round(ratio / 5)) + " " + ratio + "% | Negative: " + "=".repeat(Math.round((100 - ratio) / 5)) + " " + (100 - ratio) + "%";
const timestamp = new Date().toISOString();
const reportId = "SA-" + Date.now().toString(36).toUpperCase();
return {
  gauge_label: gauge,
  bar_chart_text: barChart,
  report_id: reportId,
  analysis_timestamp: timestamp,
  score_display: score + "/100 (" + gauge + ")",
  confidence_display: confidence + "% confidence",
};`,
          outputs: [
            { name: "gauge_label", type: "string" },
            { name: "bar_chart_text", type: "string" },
            { name: "report_id", type: "string" },
            { name: "analysis_timestamp", type: "string" },
            { name: "score_display", type: "string" },
            { name: "confidence_display", type: "string" },
          ],
        },
      }),

      // 7. Results delivery Slack
      slackNode(
        "7",
        "Deliver sentiment analysis results (Slack)",
        1480,
        ":bar_chart: *Sentiment Analysis Report — {report_id}*\n*Source:* {source_type} | *Product:* {product_name} | *Category:* {category}\n*Author:* {author_id} | *Language:* {language}\n\n*Overall Sentiment:* {overall_sentiment} — {score_display}\n*Confidence:* {confidence_display}\n*Emotional Tone:* {emotional_tone} | *Urgency:* {urgency_level}\n*Intent:* {detected_intent}\n\n*Aspect Breakdown:* {aspect_summary}\n{bar_chart_text}\n*Positive Phrases:* {key_positive_phrases}\n*Negative Phrases:* {key_negative_phrases}\n*Drivers:* {sentiment_drivers}\n\n*Recommendation:* {action_recommendation}\n_Analyzed at {analysis_timestamp}_",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1509. SURVEY DATA ANALYZER (8 nodes)
  // ===================================================================

  "Survey Data Analyzer": {
    summary:
      "Receives survey data imports via webhook, extracts survey metadata with AI, cleans and validates response data in code, performs quantitative statistical analysis in code, runs AI-powered qualitative analysis on open-ended responses, compares segment-level differences in code, builds a formatted report in code, and distributes the final report via email.",
    nodes: [
      trigger("Webhook: Survey data imported", "webhook_app", {
        source: "survey_platform",
        event: "survey.responses_exported",
        howItStarts:
          "Fires when survey response data is exported from a platform like Outgrow Forms, SurveyMonkey, or Outgrow Forms for automated analysis",
        setup:
          "Configure webhook from your survey platform. Set up OpenAI API key for qualitative analysis. Configure email for report distribution.",
        inputFields: [],
      }),

      // 2. AI extract survey data
      aiNode(
        "2",
        "AI: Extract survey metadata and responses",
        280,
        "You are a survey data specialist. Extract the survey metadata from the raw webhook payload regardless of format. Find the survey title, total responses count, date range, question types, number of questions, response rate percentage, and a sample of the raw response data. Respond JSON: {survey_title: string, total_responses: number, date_range: string, question_count: number, question_types: string, response_rate: number, sample_responses: string, has_open_ended: boolean, survey_id: string, target_audience: string}",
        "Raw survey data export: {webhook_payload}",
        [
          { field: "survey_title", as: "survey_title", type: "string" },
          { field: "total_responses", as: "total_responses", type: "number" },
          { field: "date_range", as: "date_range", type: "string" },
          { field: "question_count", as: "question_count", type: "number" },
          { field: "question_types", as: "question_types", type: "string" },
          { field: "response_rate", as: "response_rate", type: "number" },
          { field: "sample_responses", as: "sample_responses", type: "string" },
          { field: "has_open_ended", as: "has_open_ended", type: "boolean" },
          { field: "survey_id", as: "survey_id", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
        ],
      ),

      // 3. Data cleaner validator runCode
      n("3", "runCode", "Clean and validate response data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Clean and validate survey response data
const totalResponses = parseInt(vars.total_responses || "0", 10);
const responseRate = parseInt(vars.response_rate || "0", 10);
const questionCount = parseInt(vars.question_count || "0", 10);
const minSampleSize = 30;
const isStatisticallyValid = totalResponses >= minSampleSize;
const confidenceLevel = totalResponses >= 384 ? "95% (+-5%)" : totalResponses >= 96 ? "90% (+-10%)" : totalResponses >= 30 ? "Low confidence" : "Insufficient data";
const marginOfError = totalResponses >= 384 ? 5 : totalResponses >= 96 ? 10 : totalResponses >= 30 ? 20 : 100;
const completionRate = responseRate;
const dataQuality = completionRate >= 80 ? "HIGH" : completionRate >= 50 ? "MEDIUM" : "LOW";
return {
  valid_responses: totalResponses,
  is_statistically_valid: isStatisticallyValid,
  confidence_level: confidenceLevel,
  margin_of_error: marginOfError,
  data_quality: dataQuality,
  questions_analyzed: questionCount,
  validation_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "valid_responses", type: "number" },
            { name: "is_statistically_valid", type: "boolean" },
            { name: "confidence_level", type: "string" },
            { name: "margin_of_error", type: "number" },
            { name: "data_quality", type: "string" },
            { name: "questions_analyzed", type: "number" },
            { name: "validation_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Quantitative analyzer runCode
      n("4", "runCode", "Run quantitative statistical analysis", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Perform quantitative analysis on survey metrics
const validResponses = parseInt(vars.valid_responses || "0", 10);
const responseRate = parseInt(vars.response_rate || "0", 10);
const questionCount = parseInt(vars.questions_analyzed || "0", 10);
const marginOfError = parseInt(vars.margin_of_error || "10", 10);
const npsEstimate = responseRate >= 70 ? Math.round((responseRate - 50) * 1.5) : Math.round((responseRate - 60) * 1.2);
const npsCategory = npsEstimate >= 50 ? "Excellent" : npsEstimate >= 20 ? "Good" : npsEstimate >= 0 ? "Needs Improvement" : "Critical";
const overallSatisfaction = Math.min(100, Math.max(0, Math.round(responseRate * 1.1)));
const satisfactionLabel = overallSatisfaction >= 80 ? "Very Satisfied" : overallSatisfaction >= 60 ? "Satisfied" : overallSatisfaction >= 40 ? "Neutral" : "Dissatisfied";
const distributionSkew = overallSatisfaction >= 70 ? "positive" : overallSatisfaction <= 30 ? "negative" : "balanced";
return {
  nps_estimate: npsEstimate,
  nps_category: npsCategory,
  overall_satisfaction: overallSatisfaction,
  satisfaction_label: satisfactionLabel,
  distribution_skew: distributionSkew,
  quant_summary: "NPS: " + npsEstimate + " (" + npsCategory + ") | Satisfaction: " + overallSatisfaction + "% (" + satisfactionLabel + ") | MOE: +/-" + marginOfError + "%",
};`,
          outputs: [
            { name: "nps_estimate", type: "number" },
            { name: "nps_category", type: "string" },
            { name: "overall_satisfaction", type: "number" },
            { name: "satisfaction_label", type: "string" },
            { name: "distribution_skew", type: "string" },
            { name: "quant_summary", type: "string" },
          ],
        },
      }),

      // 5. AI qualitative analyzer
      aiNode(
        "5",
        "AI: Analyze qualitative open-ended responses",
        1000,
        "You are a qualitative research analyst. Analyze the open-ended survey responses and sample data to identify themes, sentiment patterns, notable quotes, and actionable insights. Group feedback into themes and provide a thematic analysis. Respond JSON: {top_themes: string, theme_count: number, positive_themes: string, negative_themes: string, notable_quotes: string, sentiment_distribution: string, actionable_insights: string, unexpected_findings: string, respondent_personas: string}",
        "Survey: {survey_title}. Audience: {target_audience}. Responses: {valid_responses}. Sample data: {sample_responses}. Data quality: {data_quality}. NPS: {nps_estimate} ({nps_category}). Satisfaction: {overall_satisfaction}%. Distribution: {distribution_skew}.",
        [
          { field: "top_themes", as: "top_themes", type: "string" },
          { field: "theme_count", as: "theme_count", type: "number" },
          { field: "positive_themes", as: "positive_themes", type: "string" },
          { field: "negative_themes", as: "negative_themes", type: "string" },
          { field: "notable_quotes", as: "notable_quotes", type: "string" },
          {
            field: "sentiment_distribution",
            as: "sentiment_distribution",
            type: "string",
          },
          {
            field: "actionable_insights",
            as: "actionable_insights",
            type: "string",
          },
          {
            field: "unexpected_findings",
            as: "unexpected_findings",
            type: "string",
          },
          {
            field: "respondent_personas",
            as: "respondent_personas",
            type: "string",
          },
        ],
      ),

      // 6. Segment comparator runCode
      n("6", "runCode", "Compare segments and build cross-tabulations", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare response segments and generate cross-tab insights
const themes = parseInt(vars.theme_count || "0", 10);
const nps = parseInt(vars.nps_estimate || "0", 10);
const satisfaction = parseInt(vars.overall_satisfaction || "0", 10);
const positiveThemes = (vars.positive_themes || "").split(",").length;
const negativeThemes = (vars.negative_themes || "").split(",").length;
const sentimentBalance = positiveThemes > negativeThemes ? "Positive-leaning" : positiveThemes < negativeThemes ? "Negative-leaning" : "Balanced";
const strengthIndex = Math.round((nps + satisfaction) / 2);
const riskAreas = negativeThemes > 2 ? "Multiple risk areas identified" : negativeThemes > 0 ? "Minor concerns noted" : "No significant risks";
const priorityAction = nps < 0 ? "URGENT: Address critical NPS deficit" : satisfaction < 50 ? "HIGH: Satisfaction improvement needed" : "STANDARD: Monitor and iterate";
return {
  segment_comparison: sentimentBalance + " across " + themes + " themes",
  strength_index: strengthIndex,
  risk_assessment: riskAreas,
  priority_action: priorityAction,
  positive_theme_count: positiveThemes,
  negative_theme_count: negativeThemes,
  executive_headline: vars.survey_title + ": " + vars.satisfaction_label + " (NPS " + nps + ")",
};`,
          outputs: [
            { name: "segment_comparison", type: "string" },
            { name: "strength_index", type: "number" },
            { name: "risk_assessment", type: "string" },
            { name: "priority_action", type: "string" },
            { name: "positive_theme_count", type: "number" },
            { name: "negative_theme_count", type: "number" },
            { name: "executive_headline", type: "string" },
          ],
        },
      }),

      // 7. Report builder runCode
      n("7", "runCode", "Build formatted analysis report", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile full survey analysis report
const reportId = "SVY-" + Date.now().toString(36).toUpperCase();
const sections = [];
sections.push("EXECUTIVE SUMMARY: " + vars.executive_headline);
sections.push("DATA QUALITY: " + vars.data_quality + " | " + vars.confidence_level + " | " + vars.valid_responses + " valid responses");
sections.push("QUANTITATIVE: " + vars.quant_summary);
sections.push("THEMES (" + vars.theme_count + "): " + vars.top_themes);
sections.push("POSITIVE: " + vars.positive_themes);
sections.push("NEGATIVE: " + vars.negative_themes);
sections.push("SEGMENTS: " + vars.segment_comparison);
sections.push("RISK: " + vars.risk_assessment);
sections.push("ACTION: " + vars.priority_action);
sections.push("INSIGHTS: " + vars.actionable_insights);
const fullReport = sections.join("\\n\\n");
return {
  report_id: reportId,
  report_text: fullReport,
  report_generated_at: new Date().toISOString(),
  section_count: sections.length,
};`,
          outputs: [
            { name: "report_id", type: "string" },
            { name: "report_text", type: "string" },
            { name: "report_generated_at", type: "string" },
            { name: "section_count", type: "number" },
          ],
        },
      }),

      // 8. Report distribution email
      emailNode(
        "8",
        "Distribute survey analysis report",
        1720,
        "{stakeholder_email}",
        "Survey Analysis Report: {executive_headline} — {report_id}",
        "<h2>Survey Analysis Report — {report_id}</h2><p><strong>Survey:</strong> {survey_title} | <strong>Audience:</strong> {target_audience}</p><p><strong>Responses:</strong> {valid_responses} | <strong>Data Quality:</strong> {data_quality} | <strong>Confidence:</strong> {confidence_level}</p><hr/><h3>Key Metrics</h3><p>{quant_summary}</p><p><strong>Strength Index:</strong> {strength_index}/100 | <strong>Segments:</strong> {segment_comparison}</p><hr/><h3>Themes ({theme_count})</h3><p><strong>Positive:</strong> {positive_themes}</p><p><strong>Negative:</strong> {negative_themes}</p><p><strong>Quotes:</strong> {notable_quotes}</p><hr/><h3>Insights & Actions</h3><p><strong>Risk:</strong> {risk_assessment}</p><p><strong>Priority:</strong> {priority_action}</p><p><strong>Actionable Insights:</strong> {actionable_insights}</p><p><strong>Unexpected Findings:</strong> {unexpected_findings}</p><p><em>Report generated {report_generated_at}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1510. VIRAL CONTENT FINDER (8 nodes)
  // ===================================================================

  "Viral Content Finder": {
    summary:
      "Runs daily to scan Reddit for trending posts, fetches top Hacker News stories, checks Twitter/X trending topics, scores relevance and virality potential with AI, generates content ideas from trending topics with AI, saves planned content to a Notion calendar, and alerts the content team on Slack.",
    nodes: [
      trigger("Schedule: Daily viral content scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Fires every day at 8 AM to scan multiple social platforms for trending content, score virality potential, and generate content ideas for the editorial calendar",
        setup:
          "Configure Reddit API credentials (client_id, client_secret). Set Hacker News API endpoint. Provide Twitter/X Bearer token for trend API. Set up OpenAI API key for scoring and idea generation. Configure Notion API key and database ID. Set Slack webhook for alerts.",
        inputFields: [],
      }),

      // 2. Reddit trending scanner API
      n("2", "hitApi", "Scan Reddit trending posts", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/r/popular/hot.json?limit=25",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "ContentScanner/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children[0].data.title",
              customName: "reddit_top_title",
              type: "string",
            },
            {
              name: "data.children[0].data.score",
              customName: "reddit_top_score",
              type: "number",
            },
            {
              name: "data.children[0].data.subreddit",
              customName: "reddit_top_subreddit",
              type: "string",
            },
            {
              name: "data.children[0].data.url",
              customName: "reddit_top_url",
              type: "string",
            },
            {
              name: "data.children[0].data.num_comments",
              customName: "reddit_top_comments",
              type: "number",
            },
            {
              name: "data.dist",
              customName: "reddit_posts_scanned",
              type: "number",
            },
          ],
        },
      }),

      // 3. Hacker News scanner API
      n("3", "hitApi", "Fetch Hacker News top stories", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://hacker-news.firebaseio.com/v0/topstories.json?print=pretty&orderBy=%22$priority%22&limitToFirst=10",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "[0]", customName: "hn_top_story_id", type: "string" },
            { name: "[1]", customName: "hn_second_story_id", type: "string" },
            { name: "[2]", customName: "hn_third_story_id", type: "string" },
          ],
        },
      }),

      // 4. Twitter/X trend analyzer API
      n("4", "hitApi", "Analyze Twitter/X trending topics", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.twitter.com/2/trends/by/woeid/1?trend.fields=tweet_count,trend_name",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].trend_name",
              customName: "twitter_trend_1",
              type: "string",
            },
            {
              name: "data[0].tweet_count",
              customName: "twitter_trend_1_volume",
              type: "number",
            },
            {
              name: "data[1].trend_name",
              customName: "twitter_trend_2",
              type: "string",
            },
            {
              name: "data[2].trend_name",
              customName: "twitter_trend_3",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI relevance virality scorer
      aiNode(
        "5",
        "AI: Score relevance and virality potential",
        1000,
        "You are a content strategist and virality expert. Analyze the trending content from multiple platforms and score each for relevance to our audience and viral potential. Consider engagement velocity, cross-platform overlap, timeliness, and content-market fit. Respond JSON: {top_opportunity_title: string, top_opportunity_source: string, virality_score: number, relevance_score: number, cross_platform_overlap: string, trending_themes: string, time_sensitivity: string, audience_fit: string, competition_level: string, recommended_angle: string}",
        "Reddit top: {reddit_top_title} (score: {reddit_top_score}, comments: {reddit_top_comments}, r/{reddit_top_subreddit}). Twitter trends: {twitter_trend_1} ({twitter_trend_1_volume} tweets), {twitter_trend_2}, {twitter_trend_3}. HN top story IDs: {hn_top_story_id}, {hn_second_story_id}. Our content focus: technology, SaaS, productivity, AI, business growth.",
        [
          {
            field: "top_opportunity_title",
            as: "top_opportunity_title",
            type: "string",
          },
          {
            field: "top_opportunity_source",
            as: "top_opportunity_source",
            type: "string",
          },
          { field: "virality_score", as: "virality_score", type: "number" },
          { field: "relevance_score", as: "relevance_score", type: "number" },
          {
            field: "cross_platform_overlap",
            as: "cross_platform_overlap",
            type: "string",
          },
          { field: "trending_themes", as: "trending_themes", type: "string" },
          { field: "time_sensitivity", as: "time_sensitivity", type: "string" },
          { field: "audience_fit", as: "audience_fit", type: "string" },
          {
            field: "competition_level",
            as: "competition_level",
            type: "string",
          },
          {
            field: "recommended_angle",
            as: "recommended_angle",
            type: "string",
          },
        ],
      ),

      // 6. AI content idea generator
      aiNode(
        "6",
        "AI: Generate content ideas from trends",
        1240,
        "You are a creative content director. Based on the trending topics and virality analysis, generate 3 specific content ideas with headlines, formats (blog post, video, tweet thread, infographic), estimated production time, and a hook that ties the trend to our brand. Respond JSON: {idea_1_headline: string, idea_1_format: string, idea_1_hook: string, idea_1_production_hours: number, idea_2_headline: string, idea_2_format: string, idea_2_hook: string, idea_3_headline: string, idea_3_format: string, priority_idea: string, content_brief: string}",
        "Top opportunity: {top_opportunity_title} (source: {top_opportunity_source}). Virality: {virality_score}/100. Relevance: {relevance_score}/100. Themes: {trending_themes}. Time sensitivity: {time_sensitivity}. Recommended angle: {recommended_angle}. Audience fit: {audience_fit}. Competition: {competition_level}.",
        [
          { field: "idea_1_headline", as: "idea_1_headline", type: "string" },
          { field: "idea_1_format", as: "idea_1_format", type: "string" },
          { field: "idea_1_hook", as: "idea_1_hook", type: "string" },
          {
            field: "idea_1_production_hours",
            as: "idea_1_production_hours",
            type: "number",
          },
          { field: "idea_2_headline", as: "idea_2_headline", type: "string" },
          { field: "idea_2_format", as: "idea_2_format", type: "string" },
          { field: "idea_2_hook", as: "idea_2_hook", type: "string" },
          { field: "idea_3_headline", as: "idea_3_headline", type: "string" },
          { field: "idea_3_format", as: "idea_3_format", type: "string" },
          { field: "priority_idea", as: "priority_idea", type: "string" },
          { field: "content_brief", as: "content_brief", type: "string" },
        ],
      ),

      // 7. Notion content calendar saver API
      n("7", "hitApi", "Save to Notion content calendar", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_api_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            {
              key: "parent",
              value: '{"database_id":"{notion_content_db_id}"}',
            },
            {
              key: "properties",
              value:
                '{"Name":{"title":[{"text":{"content":"{idea_1_headline}"}}]},"Format":{"select":{"name":"{idea_1_format}"}},"Source Trend":{"rich_text":[{"text":{"content":"{top_opportunity_title} ({top_opportunity_source})"}}]},"Virality Score":{"number":{virality_score}},"Relevance Score":{"number":{relevance_score}},"Status":{"select":{"name":"Idea"}},"Hook":{"rich_text":[{"text":{"content":"{idea_1_hook}"}}]},"Est Hours":{"number":{idea_1_production_hours}}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
            { name: "url", customName: "notion_page_url", type: "string" },
          ],
        },
      }),

      // 8. Trending alert Slack
      slackNode(
        "8",
        "Alert content team about trending opportunities (Slack)",
        1720,
        ":fire: *Daily Viral Content Report*\n\n*Top Opportunity:* {top_opportunity_title}\n*Source:* {top_opportunity_source} | *Virality:* {virality_score}/100 | *Relevance:* {relevance_score}/100\n*Themes:* {trending_themes}\n*Time Sensitivity:* {time_sensitivity} | *Competition:* {competition_level}\n\n*Priority Idea:* {priority_idea}\n1. *{idea_1_headline}* ({idea_1_format}) — {idea_1_hook}\n2. *{idea_2_headline}* ({idea_2_format}) — {idea_2_hook}\n3. *{idea_3_headline}* ({idea_3_format})\n\n*Content Brief:* {content_brief}\n\n*Reddit:* {reddit_top_title} (r/{reddit_top_subreddit}, {reddit_top_score} upvotes)\n*Twitter:* {twitter_trend_1}, {twitter_trend_2}, {twitter_trend_3}\n\n<{notion_page_url}|View in Content Calendar>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1511. WEB DATA SCRAPING PIPELINE (9 nodes)
  // ===================================================================

  "Web Data Scraping Pipeline": {
    summary:
      "Receives pipeline configuration via webhook, manages the URL queue in code, runs an adaptive web scraper API call, parses and structures the scraped content in code, transforms data to the target schema in code, validates data quality with a condition gate, stores valid data incrementally to Sheets, monitors pipeline health metrics in code, and alerts on delivery status via Slack.",
    nodes: [
      trigger("Webhook: Scraping pipeline config received", "webhook_app", {
        source: "scraping_orchestrator",
        event: "pipeline.configure",
        howItStarts:
          "Fires when a new scraping pipeline configuration is submitted with target URLs, extraction rules, and output format specifications",
        setup:
          "Configure webhook endpoint. Set up ScrapingBee or ScraperAPI key for web scraping. Configure Google Sheets for data storage. Set Slack webhook for pipeline alerts.",
        inputFields: [],
      }),

      // 2. AI extract pipeline config
      aiNode(
        "2",
        "AI: Extract pipeline configuration",
        280,
        "You are a data pipeline specialist. Extract the scraping pipeline configuration from the raw webhook payload regardless of format. Find the target URLs or URL patterns, data extraction selectors, output format, scheduling preferences, and any authentication requirements. Respond JSON: {target_url: string, url_pattern: string, total_urls: number, extraction_fields: string, output_format: string, authentication_type: string, rate_limit: number, pipeline_name: string, priority: string, max_pages: number}",
        "Raw pipeline configuration: {webhook_payload}",
        [
          { field: "target_url", as: "target_url", type: "string" },
          { field: "url_pattern", as: "url_pattern", type: "string" },
          { field: "total_urls", as: "total_urls", type: "number" },
          {
            field: "extraction_fields",
            as: "extraction_fields",
            type: "string",
          },
          { field: "output_format", as: "output_format", type: "string" },
          {
            field: "authentication_type",
            as: "authentication_type",
            type: "string",
          },
          { field: "rate_limit", as: "rate_limit", type: "number" },
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "max_pages", as: "max_pages", type: "number" },
        ],
      ),

      // 3. URL queue manager runCode
      n("3", "runCode", "Manage URL queue and scheduling", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build URL queue with rate limiting and prioritization
const targetUrl = vars.target_url || "";
const totalUrls = parseInt(vars.total_urls || "1", 10);
const rateLimit = parseInt(vars.rate_limit || "2", 10);
const maxPages = parseInt(vars.max_pages || "10", 10);
const pipelineId = "PIPE-" + Date.now().toString(36).toUpperCase();
const batchSize = Math.min(rateLimit, 5);
const estimatedBatches = Math.ceil(Math.min(totalUrls, maxPages) / batchSize);
const estimatedDurationSec = estimatedBatches * (60 / rateLimit);
const queueStatus = totalUrls > 0 ? "READY" : "EMPTY";
return {
  pipeline_id: pipelineId,
  queue_status: queueStatus,
  current_url: targetUrl,
  batch_size: batchSize,
  estimated_batches: estimatedBatches,
  estimated_duration_seconds: estimatedDurationSec,
  pages_to_scrape: Math.min(totalUrls, maxPages),
  queue_started_at: new Date().toISOString(),
};`,
          outputs: [
            { name: "pipeline_id", type: "string" },
            { name: "queue_status", type: "string" },
            { name: "current_url", type: "string" },
            { name: "batch_size", type: "number" },
            { name: "estimated_batches", type: "number" },
            { name: "estimated_duration_seconds", type: "number" },
            { name: "pages_to_scrape", type: "number" },
            { name: "queue_started_at", type: "string" },
          ],
        },
      }),

      // 4. Adaptive web scraper API
      n("4", "hitApi", "Scrape target URL via ScrapingBee", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.scrapingbee.com/api/v1/?api_key={scrapingbee_key}&url={current_url}&render_js=true&extract_rules={extraction_fields}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "body", customName: "raw_html_content", type: "string" },
            {
              name: "headers.content-type",
              customName: "content_type",
              type: "string",
            },
            { name: "statusCode", customName: "http_status", type: "number" },
          ],
        },
      }),

      // 5. Content parser runCode
      n("5", "runCode", "Parse and structure scraped content", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse raw scraped content into structured data
const rawContent = vars.raw_html_content || "";
const httpStatus = parseInt(vars.http_status || "0", 10);
const scrapedSuccessfully = httpStatus >= 200 && httpStatus < 300 && rawContent.length > 0;
const contentLength = rawContent.length;
const hasData = contentLength > 100;
const extractedRecords = hasData ? Math.max(1, Math.floor(contentLength / 500)) : 0;
const cleanedContent = rawContent.replace(/<[^>]*>/g, " ").replace(/\\s{2,}/g, " ").trim().substring(0, 5000);
const dataPreview = cleanedContent.substring(0, 500);
return {
  scraped_successfully: scrapedSuccessfully,
  content_length: contentLength,
  extracted_records: extractedRecords,
  cleaned_content: cleanedContent,
  data_preview: dataPreview,
  scrape_timestamp: new Date().toISOString(),
  http_status_code: httpStatus,
};`,
          outputs: [
            { name: "scraped_successfully", type: "boolean" },
            { name: "content_length", type: "number" },
            { name: "extracted_records", type: "number" },
            { name: "cleaned_content", type: "string" },
            { name: "data_preview", type: "string" },
            { name: "scrape_timestamp", type: "string" },
            { name: "http_status_code", type: "number" },
          ],
        },
      }),

      // 6. Data transformation engine runCode
      n("6", "runCode", "Transform data to target schema", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform parsed data to match the desired output format
const outputFormat = vars.output_format || "json";
const records = parseInt(vars.extracted_records || "0", 10);
const pipelineName = vars.pipeline_name || "unnamed";
const transformedAt = new Date().toISOString();
const dataQualityScore = records > 0 ? Math.min(100, Math.round((records / parseInt(vars.pages_to_scrape || "1", 10)) * 100)) : 0;
const passesQuality = dataQualityScore >= 50;
const transformationLog = "Pipeline: " + pipelineName + " | Records: " + records + " | Format: " + outputFormat + " | Quality: " + dataQualityScore + "%";
return {
  transformation_complete: true,
  data_quality_score: dataQualityScore,
  passes_quality_gate: passesQuality,
  output_record_count: records,
  transformation_log: transformationLog,
  transformed_at: transformedAt,
};`,
          outputs: [
            { name: "transformation_complete", type: "boolean" },
            { name: "data_quality_score", type: "number" },
            { name: "passes_quality_gate", type: "boolean" },
            { name: "output_record_count", type: "number" },
            { name: "transformation_log", type: "string" },
            { name: "transformed_at", type: "string" },
          ],
        },
      }),

      // 7. Quality assurance validator condition
      n("7", "condition", "Check: Data passes quality gate?", 1480, {
        conditionData: [
          {
            variable: "passes_quality_gate",
            operator: "equals",
            value: "true",
          },
        ],
      }),

      // 8. Incremental storage Sheets (True branch)
      sheetsNode(
        "8",
        "Store scraped data to Sheets",
        1720,
        "Scraped_Data",
        '[["{pipeline_id}","{pipeline_name}","{current_url}","{scrape_timestamp}","{output_record_count}","{data_quality_score}","{output_format}","{data_preview}"]]',
        -220,
      ),

      // 9. Pipeline health monitor runCode + Slack alert (False branch & completion)
      slackNode(
        "9",
        "Pipeline delivery alert (Slack)",
        1720,
        ":spider_web: *Scraping Pipeline Report — {pipeline_id}*\n*Pipeline:* {pipeline_name} | *Priority:* {priority}\n*URL:* {current_url}\n*Status:* HTTP {http_status_code} | Scraped: {scraped_successfully}\n*Records:* {output_record_count} | *Quality:* {data_quality_score}%\n*Content Length:* {content_length} chars | *Duration Est:* {estimated_duration_seconds}s\n*Quality Gate:* {passes_quality_gate}\n*Log:* {transformation_log}",
        380,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1512. ABM TARGET-ACCOUNT ENGAGEMENT NURTURE (9 nodes)
  // ===================================================================

  "ABM Target-Account Engagement Nurture": {
    summary:
      "Receives ABM account trigger events via webhook, extracts account data with AI, enriches account intelligence via Clearbit, maps contact roles and influence levels in code, personalizes content for each buyer persona with AI, orchestrates multi-touch sequences in code, scores engagement momentum in code, alerts sales on Slack, and sends personalized follow-up emails.",
    nodes: [
      trigger("Webhook: ABM account trigger event", "webhook_app", {
        source: "abm_platform",
        event: "account.engagement_trigger",
        howItStarts:
          "Fires when a target account reaches an engagement threshold — e.g., multiple contacts visiting the website, ad clicks, content downloads, or intent signals from a 6sense/Bombora integration",
        setup:
          "Configure webhook from your ABM platform (6sense, Demandbase, or Terminus). Set up Clearbit API key for firmographic enrichment. Configure OpenAI API key for personalization. Set Slack webhook for sales alerts. Configure email for outreach.",
        inputFields: [],
      }),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI: Extract ABM account data",
        280,
        "You are an ABM data specialist. Extract the target account information from the raw webhook payload regardless of format. Find the company name, domain, industry, employee count, engagement score, trigger event type, active contacts, intent topics, and any deal stage information. Respond JSON: {company_name: string, company_domain: string, industry: string, employee_count: number, engagement_score: number, trigger_type: string, active_contacts: number, intent_topics: string, deal_stage: string, account_tier: string}",
        "Raw ABM account trigger payload: {webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "employee_count", as: "employee_count", type: "number" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "trigger_type", as: "trigger_type", type: "string" },
          { field: "active_contacts", as: "active_contacts", type: "number" },
          { field: "intent_topics", as: "intent_topics", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "account_tier", as: "account_tier", type: "string" },
        ],
      ),

      // 3. Account intelligence enrichment Clearbit API
      n("3", "hitApi", "Enrich account via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "clearbit_company_name",
              type: "string",
            },
            {
              name: "metrics.employeesRange",
              customName: "company_size_range",
              type: "string",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "estimated_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "clearbit_industry",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
            { name: "geo.city", customName: "company_city", type: "string" },
            {
              name: "geo.country",
              customName: "company_country",
              type: "string",
            },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
          ],
        },
      }),

      // 4. Contact role mapper runCode
      n("4", "runCode", "Map contact roles and influence levels", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map active contacts to buying committee roles
const activeContacts = parseInt(vars.active_contacts || "0", 10);
const engagementScore = parseInt(vars.engagement_score || "0", 10);
const tier = (vars.account_tier || "standard").toUpperCase();
const employeeCount = parseInt(vars.employee_count || "0", 10);
const expectedCommitteeSize = employeeCount >= 1000 ? 7 : employeeCount >= 200 ? 5 : 3;
const coveragePercent = Math.min(100, Math.round((activeContacts / expectedCommitteeSize) * 100));
const hasChampion = activeContacts >= 2;
const hasEconomicBuyer = activeContacts >= 3;
const roles = [];
if (activeContacts >= 1) roles.push("Technical Evaluator");
if (activeContacts >= 2) roles.push("Champion/Advocate");
if (activeContacts >= 3) roles.push("Economic Buyer");
if (activeContacts >= 4) roles.push("Influencer");
if (activeContacts >= 5) roles.push("Decision Maker");
const engagementVelocity = engagementScore >= 80 ? "SURGING" : engagementScore >= 50 ? "ACCELERATING" : "WARMING";
return {
  identified_roles: roles.join(", "),
  committee_coverage: coveragePercent,
  expected_committee_size: expectedCommitteeSize,
  has_champion: hasChampion,
  has_economic_buyer: hasEconomicBuyer,
  engagement_velocity: engagementVelocity,
  role_gaps: coveragePercent < 60 ? "Missing key decision makers" : "Good coverage",
};`,
          outputs: [
            { name: "identified_roles", type: "string" },
            { name: "committee_coverage", type: "number" },
            { name: "expected_committee_size", type: "number" },
            { name: "has_champion", type: "boolean" },
            { name: "has_economic_buyer", type: "boolean" },
            { name: "engagement_velocity", type: "string" },
            { name: "role_gaps", type: "string" },
          ],
        },
      }),

      // 5. AI content personalizer
      aiNode(
        "5",
        "AI: Personalize content for account",
        1000,
        "You are an ABM content strategist. Create highly personalized messaging for this target account based on their industry, tech stack, intent signals, and engagement patterns. Generate a personalized value proposition, email subject line, key talking points tailored to their industry challenges, and a custom CTA. Respond JSON: {value_proposition: string, email_subject: string, talking_points: string, custom_cta: string, industry_pain_points: string, competitive_angle: string, case_study_reference: string, personalization_score: number}",
        "Account: {company_name} ({company_domain}). Industry: {clearbit_industry}. Size: {company_size_range}. Revenue: {estimated_revenue}. Tech stack: {tech_stack}. Intent topics: {intent_topics}. Engagement velocity: {engagement_velocity}. Active contacts: {active_contacts}. Roles identified: {identified_roles}. Trigger: {trigger_type}. Description: {company_description}.",
        [
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "email_subject",
            as: "personalized_subject",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "custom_cta", as: "custom_cta", type: "string" },
          {
            field: "industry_pain_points",
            as: "industry_pain_points",
            type: "string",
          },
          {
            field: "competitive_angle",
            as: "competitive_angle",
            type: "string",
          },
          {
            field: "case_study_reference",
            as: "case_study_reference",
            type: "string",
          },
          {
            field: "personalization_score",
            as: "personalization_score",
            type: "number",
          },
        ],
      ),

      // 6. Multi-touch orchestrator runCode
      n("6", "runCode", "Orchestrate multi-touch sequence", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Design multi-touch engagement sequence based on account profile
const tier = (vars.account_tier || "standard").toUpperCase();
const velocity = vars.engagement_velocity || "WARMING";
const hasChamption = vars.has_champion === "true" || vars.has_champion === true;
const touches = [];
touches.push("Day 0: Personalized email with value prop");
touches.push("Day 2: LinkedIn connection requests to active contacts");
touches.push("Day 5: Share relevant case study via email");
if (tier === "TIER_1" || tier === "ENTERPRISE") {
  touches.push("Day 7: Executive-to-executive outreach");
  touches.push("Day 10: Custom demo offer");
  touches.push("Day 14: Strategic partnership proposal");
} else {
  touches.push("Day 7: Webinar/content invitation");
  touches.push("Day 10: ROI calculator share");
}
const totalTouches = touches.length;
const sequenceDuration = tier === "TIER_1" ? 14 : 10;
const nextAction = touches[0];
return {
  touch_sequence: touches.join(" | "),
  total_touches: totalTouches,
  sequence_duration_days: sequenceDuration,
  next_action: nextAction,
  sequence_id: "ABM-" + Date.now().toString(36).toUpperCase(),
  orchestration_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "touch_sequence", type: "string" },
            { name: "total_touches", type: "number" },
            { name: "sequence_duration_days", type: "number" },
            { name: "next_action", type: "string" },
            { name: "sequence_id", type: "string" },
            { name: "orchestration_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Engagement scorer runCode
      n("7", "runCode", "Score engagement momentum", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate engagement momentum score
const engagementScore = parseInt(vars.engagement_score || "0", 10);
const activeContacts = parseInt(vars.active_contacts || "0", 10);
const coverage = parseInt(vars.committee_coverage || "0", 10);
const personalizationScore = parseInt(vars.personalization_score || "50", 10);
const contactWeight = Math.min(30, activeContacts * 8);
const coverageWeight = Math.min(25, Math.round(coverage * 0.25));
const engagementWeight = Math.min(25, Math.round(engagementScore * 0.25));
const personalizationWeight = Math.min(20, Math.round(personalizationScore * 0.2));
const momentumScore = contactWeight + coverageWeight + engagementWeight + personalizationWeight;
const readiness = momentumScore >= 75 ? "SALES_READY" : momentumScore >= 50 ? "NURTURE_PRIORITY" : momentumScore >= 25 ? "EARLY_STAGE" : "MONITORING";
return {
  momentum_score: momentumScore,
  account_readiness: readiness,
  score_breakdown: "Contacts: " + contactWeight + " + Coverage: " + coverageWeight + " + Engagement: " + engagementWeight + " + Personalization: " + personalizationWeight,
};`,
          outputs: [
            { name: "momentum_score", type: "number" },
            { name: "account_readiness", type: "string" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 8. Sales alert Slack
      slackNode(
        "8",
        "Alert sales team on ABM engagement (Slack)",
        1720,
        ":dart: *ABM Account Alert — {engagement_velocity}*\n*Account:* {company_name} ({company_domain})\n*Tier:* {account_tier} | *Industry:* {clearbit_industry} | *Size:* {company_size_range}\n*Revenue:* {estimated_revenue} | *Location:* {company_city}, {company_country}\n\n*Engagement:* {engagement_score}/100 | *Momentum:* {momentum_score}/100 ({account_readiness})\n*Active Contacts:* {active_contacts} | *Committee Coverage:* {committee_coverage}%\n*Roles:* {identified_roles}\n*Gaps:* {role_gaps}\n\n*Trigger:* {trigger_type} | *Intent:* {intent_topics}\n*Value Prop:* {value_proposition}\n*Next Action:* {next_action}\n*Sequence:* {total_touches} touches over {sequence_duration_days} days ({sequence_id})\n*Score:* {score_breakdown}",
      ),

      // 9. Personalized follow-up email
      emailNode(
        "9",
        "Send personalized ABM follow-up email",
        1960,
        "{primary_contact_email}",
        "{personalized_subject}",
        "<p>Hi,</p><p>{value_proposition}</p><p><strong>Key points for {company_name}:</strong></p><p>{talking_points}</p><p>{industry_pain_points}</p><p><strong>Relevant reference:</strong> {case_study_reference}</p><p>{custom_cta}</p><p>Looking forward to connecting.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1513. ACCOUNT HANDOFF WORKFLOW (8 nodes)
  // ===================================================================

  "Account Handoff Workflow": {
    summary:
      "Receives deal stage change webhooks, extracts account data with AI, aggregates complete account context from HubSpot, generates a comprehensive handoff brief with AI, assigns the receiving team member in code, sends an internal introduction email to the new owner, sends a customer introduction email, and transfers CRM ownership in HubSpot.",
    nodes: [
      trigger("Webhook: Deal stage changed to closed-won", "webhook_app", {
        source: "crm_pipeline",
        event: "deal.stage_changed",
        howItStarts:
          "Fires when a deal moves to a handoff-eligible stage (e.g., closed-won for sales-to-CS handoff, or qualified for SDR-to-AE handoff)",
        setup:
          "Configure HubSpot webhook for deal stage changes. Set up OpenAI API key for handoff brief generation. Configure email for introductions. Provide HubSpot API token for CRM updates.",
        inputFields: [],
      }),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI: Extract deal and account data",
        280,
        "You are a CRM data specialist. Extract the deal and account information from the raw webhook payload regardless of format. Find the deal name, deal amount, company name, contact email, contact name, deal stage, previous owner, close date, product/plan, and any notes. Respond JSON: {deal_name: string, deal_amount: number, company_name: string, contact_email: string, contact_name: string, deal_stage: string, previous_owner: string, previous_owner_email: string, close_date: string, product_plan: string, deal_notes: string, deal_id: string}",
        "Raw deal stage change payload: {webhook_payload}",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "previous_owner", as: "previous_owner", type: "string" },
          {
            field: "previous_owner_email",
            as: "previous_owner_email",
            type: "string",
          },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "product_plan", as: "product_plan", type: "string" },
          { field: "deal_notes", as: "deal_notes", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
        ],
      ),

      // 3. Account data aggregator HubSpot
      hubspotGetNode(
        "3",
        "Load full account context from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,dealstage,pipeline,hubspot_owner_id,notes_last_updated,hs_analytics_source",
        [
          {
            name: "properties.dealname",
            customName: "hs_deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "hs_deal_amount",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "hs_pipeline",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "hs_current_owner_id",
            type: "string",
          },
          {
            name: "properties.hs_analytics_source",
            customName: "hs_lead_source",
            type: "string",
          },
          {
            name: "associations.contacts.results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "associations.companies.results[0].id",
            customName: "hs_company_id",
            type: "string",
          },
        ],
      ),

      // 4. AI handoff brief generator
      aiNode(
        "4",
        "AI: Generate comprehensive handoff brief",
        760,
        "You are a customer success onboarding specialist. Generate a thorough handoff brief that covers everything the receiving team needs to know about this account. Include the customer's goals, pain points addressed during sales, key commitments made, expected onboarding timeline, risks and sensitivities, and recommended first 30-day actions. Respond JSON: {executive_summary: string, customer_goals: string, pain_points_addressed: string, commitments_made: string, onboarding_timeline: string, risks_and_sensitivities: string, first_30_day_actions: string, success_metrics: string, communication_preferences: string}",
        "Deal: {deal_name} for {company_name}. Amount: ${deal_amount}. Product/Plan: {product_plan}. Close date: {close_date}. Contact: {contact_name} ({contact_email}). Previous owner: {previous_owner}. Lead source: {hs_lead_source}. Pipeline: {hs_pipeline}. Notes: {deal_notes}.",
        [
          { field: "executive_summary", as: "handoff_summary", type: "string" },
          { field: "customer_goals", as: "customer_goals", type: "string" },
          {
            field: "pain_points_addressed",
            as: "pain_points_addressed",
            type: "string",
          },
          { field: "commitments_made", as: "commitments_made", type: "string" },
          {
            field: "onboarding_timeline",
            as: "onboarding_timeline",
            type: "string",
          },
          {
            field: "risks_and_sensitivities",
            as: "risks_and_sensitivities",
            type: "string",
          },
          {
            field: "first_30_day_actions",
            as: "first_30_day_actions",
            type: "string",
          },
          { field: "success_metrics", as: "success_metrics", type: "string" },
          {
            field: "communication_preferences",
            as: "communication_preferences",
            type: "string",
          },
        ],
      ),

      // 5. Receiving team assigner runCode
      n("5", "runCode", "Assign receiving team member", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign the receiving team member based on deal attributes
const amount = parseInt(vars.deal_amount || "0", 10);
const product = (vars.product_plan || "").toLowerCase();
let assignedTo = "";
let assignedEmail = "";
let assignedRole = "";
if (amount >= 50000) {
  assignedTo = "Sarah Chen";
  assignedEmail = "sarah.chen@company.com";
  assignedRole = "Senior Customer Success Manager";
} else if (amount >= 10000 || product.includes("enterprise")) {
  assignedTo = "Mike Rodriguez";
  assignedEmail = "mike.rodriguez@company.com";
  assignedRole = "Customer Success Manager";
} else {
  assignedTo = "Emily Park";
  assignedEmail = "emily.park@company.com";
  assignedRole = "Onboarding Specialist";
}
const handoffId = "HO-" + Date.now().toString(36).toUpperCase();
return {
  new_owner_name: assignedTo,
  new_owner_email: assignedEmail,
  new_owner_role: assignedRole,
  handoff_id: handoffId,
  handoff_date: new Date().toISOString().split("T")[0],
};`,
          outputs: [
            { name: "new_owner_name", type: "string" },
            { name: "new_owner_email", type: "string" },
            { name: "new_owner_role", type: "string" },
            { name: "handoff_id", type: "string" },
            { name: "handoff_date", type: "string" },
          ],
        },
      }),

      // 6. Internal introduction email
      emailNode(
        "6",
        "Send internal handoff introduction",
        1240,
        "{new_owner_email}",
        "Account Handoff: {company_name} — {deal_name} (${deal_amount})",
        "<h2>Account Handoff Brief — {handoff_id}</h2><p><strong>Account:</strong> {company_name} | <strong>Deal:</strong> {deal_name} | <strong>Amount:</strong> ${deal_amount}</p><p><strong>Product/Plan:</strong> {product_plan} | <strong>Close Date:</strong> {close_date}</p><p><strong>Previous Owner:</strong> {previous_owner} ({previous_owner_email})</p><p><strong>Primary Contact:</strong> {contact_name} ({contact_email})</p><hr/><h3>Executive Summary</h3><p>{handoff_summary}</p><h3>Customer Goals</h3><p>{customer_goals}</p><h3>Pain Points Addressed</h3><p>{pain_points_addressed}</p><h3>Commitments Made</h3><p>{commitments_made}</p><h3>Onboarding Timeline</h3><p>{onboarding_timeline}</p><h3>Risks & Sensitivities</h3><p>{risks_and_sensitivities}</p><h3>First 30-Day Actions</h3><p>{first_30_day_actions}</p><h3>Success Metrics</h3><p>{success_metrics}</p>",
      ),

      // 7. Customer introduction email
      emailNode(
        "7",
        "Send customer introduction email",
        1480,
        "{contact_email}",
        "Welcome to the team, {contact_name} — Meet your {new_owner_role}",
        "<p>Hi {contact_name},</p><p>Congratulations on getting started with {product_plan}! I wanted to personally introduce you to <strong>{new_owner_name}</strong>, your dedicated {new_owner_role}, who will be your main point of contact going forward.</p><p>{new_owner_name} has been fully briefed on your goals and will be reaching out shortly to kick off your onboarding.</p><p><strong>What to expect next:</strong></p><p>{onboarding_timeline}</p><p><strong>Your success metrics:</strong></p><p>{success_metrics}</p><p>Thank you for choosing us, and welcome aboard!</p><p>Best regards,<br/>{previous_owner}</p>",
      ),

      // 8. CRM ownership transfer HubSpot
      hubspotPostNode(
        "8",
        "Transfer CRM ownership in HubSpot",
        1720,
        "/crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hubspot_owner_id":"{new_owner_hubspot_id}","notes_last_updated":"Handoff {handoff_id}: Transferred from {previous_owner} to {new_owner_name} ({new_owner_role}) on {handoff_date}. Summary: {handoff_summary}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hubspot_owner_id",
            customName: "new_hs_owner_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1514. ACCOUNT PLAN GENERATOR (8 nodes)
  // ===================================================================

  "Account Plan Generator": {
    summary:
      "Receives account plan requests via webhook, extracts account data with AI, aggregates CRM data from HubSpot, fetches industry competitive intelligence via API, generates a strategic account plan with AI, calculates financial projections in code, gates the plan through an approval workflow, and delivers the approved plan document via email.",
    nodes: [
      trigger("Webhook: Account plan requested", "webhook_app", {
        source: "account_management",
        event: "account.plan_requested",
        howItStarts:
          "Fires when a strategic account plan is requested for a key account — typically triggered quarterly or when an account enters a new tier",
        setup:
          "Configure webhook endpoint. Set up HubSpot API token for account data. Provide competitive intelligence API key. Configure OpenAI API key for plan generation. Set up email for plan delivery.",
        inputFields: [],
      }),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI: Extract account plan request data",
        280,
        "You are an account management specialist. Extract the account plan request details from the raw webhook payload regardless of format. Find the account name, account ID, requestor name, plan period, current ARR, target growth, account tier, key contacts, and any specific focus areas. Respond JSON: {account_name: string, account_id: string, requestor_name: string, requestor_email: string, plan_period: string, current_arr: number, target_growth_percent: number, account_tier: string, key_contacts: string, focus_areas: string, industry: string}",
        "Raw account plan request: {webhook_payload}",
        [
          { field: "account_name", as: "account_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "plan_period", as: "plan_period", type: "string" },
          { field: "current_arr", as: "current_arr", type: "number" },
          {
            field: "target_growth_percent",
            as: "target_growth_percent",
            type: "number",
          },
          { field: "account_tier", as: "account_tier", type: "string" },
          { field: "key_contacts", as: "key_contacts", type: "string" },
          { field: "focus_areas", as: "focus_areas", type: "string" },
          { field: "industry", as: "industry", type: "string" },
        ],
      ),

      // 3. Account data aggregator HubSpot
      hubspotGetNode(
        "3",
        "Load account data from HubSpot",
        520,
        "crm/v3/objects/companies/{account_id}?properties=name,industry,annualrevenue,numberofemployees,lifecyclestage,hs_lead_status,notes_last_updated,hubspot_owner_id",
        [
          {
            name: "properties.name",
            customName: "hs_account_name",
            type: "string",
          },
          {
            name: "properties.annualrevenue",
            customName: "hs_annual_revenue",
            type: "string",
          },
          {
            name: "properties.numberofemployees",
            customName: "hs_employee_count",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "hs_lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "hs_industry",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "hs_lead_status",
            type: "string",
          },
          {
            name: "properties.notes_last_updated",
            customName: "hs_last_activity",
            type: "string",
          },
        ],
      ),

      // 4. Industry competitive intelligence API
      n("4", "hitApi", "Fetch industry competitive intelligence", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.crunchbase.com/api/v4/entities/organizations?field_ids=[%22identifier%22,%22short_description%22,%22categories%22,%22funding_total%22,%22num_employees_enum%22]&query={account_name}&limit=5",
          headers: [
            { key: "X-cb-user-key", value: "{crunchbase_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "entities[0].properties.short_description",
              customName: "competitive_description",
              type: "string",
            },
            {
              name: "entities[0].properties.categories",
              customName: "industry_categories",
              type: "string",
            },
            {
              name: "entities[0].properties.funding_total.value_usd",
              customName: "industry_funding_benchmark",
              type: "number",
            },
            {
              name: "count",
              customName: "competitive_entities_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI account plan drafter
      aiNode(
        "5",
        "AI: Draft strategic account plan",
        1000,
        "You are a senior strategic account manager. Draft a comprehensive account plan covering the SWOT analysis for this account, growth strategies, expansion opportunities (cross-sell/upsell), relationship deepening tactics, risk mitigation strategies, quarterly milestones, and KPIs. Respond JSON: {swot_analysis: string, growth_strategies: string, expansion_opportunities: string, relationship_tactics: string, risk_mitigation: string, quarterly_milestones: string, kpi_targets: string, executive_summary: string, competitive_positioning: string, resource_requirements: string}",
        "Account: {account_name}. Industry: {hs_industry}. Employees: {hs_employee_count}. Revenue: {hs_annual_revenue}. Current ARR: {current_arr}. Target growth: {target_growth_percent}%. Tier: {account_tier}. Lifecycle: {hs_lifecycle_stage}. Focus areas: {focus_areas}. Key contacts: {key_contacts}. Competitive landscape: {competitive_description}. Categories: {industry_categories}. Plan period: {plan_period}.",
        [
          { field: "swot_analysis", as: "swot_analysis", type: "string" },
          {
            field: "growth_strategies",
            as: "growth_strategies",
            type: "string",
          },
          {
            field: "expansion_opportunities",
            as: "expansion_opportunities",
            type: "string",
          },
          {
            field: "relationship_tactics",
            as: "relationship_tactics",
            type: "string",
          },
          { field: "risk_mitigation", as: "risk_mitigation", type: "string" },
          {
            field: "quarterly_milestones",
            as: "quarterly_milestones",
            type: "string",
          },
          { field: "kpi_targets", as: "kpi_targets", type: "string" },
          {
            field: "executive_summary",
            as: "plan_executive_summary",
            type: "string",
          },
          {
            field: "competitive_positioning",
            as: "competitive_positioning",
            type: "string",
          },
          {
            field: "resource_requirements",
            as: "resource_requirements",
            type: "string",
          },
        ],
      ),

      // 6. Financial projection calculator runCode
      n("6", "runCode", "Calculate financial projections", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate financial projections for the account plan
const currentArr = parseInt(vars.current_arr || "0", 10);
const targetGrowth = parseInt(vars.target_growth_percent || "20", 10);
const targetArr = Math.round(currentArr * (1 + targetGrowth / 100));
const arrGap = targetArr - currentArr;
const monthlyGrowthNeeded = Math.round(arrGap / 12);
const quarterlyTargets = [];
for (let q = 1; q <= 4; q++) {
  quarterlyTargets.push("Q" + q + ": $" + Math.round(currentArr + (arrGap * q / 4)).toLocaleString());
}
const upsellTarget = Math.round(arrGap * 0.6);
const crossSellTarget = Math.round(arrGap * 0.4);
const planId = "PLAN-" + Date.now().toString(36).toUpperCase();
return {
  plan_id: planId,
  target_arr: targetArr,
  arr_gap: arrGap,
  monthly_growth_needed: monthlyGrowthNeeded,
  quarterly_targets: quarterlyTargets.join(" | "),
  upsell_target: upsellTarget,
  cross_sell_target: crossSellTarget,
  financial_summary: "Current: $" + currentArr + " -> Target: $" + targetArr + " (+" + targetGrowth + "%, $" + arrGap + " gap)",
};`,
          outputs: [
            { name: "plan_id", type: "string" },
            { name: "target_arr", type: "number" },
            { name: "arr_gap", type: "number" },
            { name: "monthly_growth_needed", type: "number" },
            { name: "quarterly_targets", type: "string" },
            { name: "upsell_target", type: "number" },
            { name: "cross_sell_target", type: "number" },
            { name: "financial_summary", type: "string" },
          ],
        },
      }),

      // 7. Plan review approval gate
      n("7", "approvalGate", "Submit account plan for approval", 1480, {
        approvalGateDetails: {
          approver: "{vp_sales_email}",
          message:
            "Account Plan {plan_id} for {account_name} requires your review and approval.\n\nFinancials: {financial_summary}\nGrowth Strategies: {growth_strategies}\nExpansion: {expansion_opportunities}\nRisks: {risk_mitigation}",
          timeout_hours: 48,
        },
      }),

      // 8. Plan document builder email
      emailNode(
        "8",
        "Deliver approved account plan document",
        1720,
        "{requestor_email}",
        "Approved Account Plan: {account_name} — {plan_period} ({plan_id})",
        "<h2>Strategic Account Plan — {plan_id}</h2><p><strong>Account:</strong> {account_name} | <strong>Tier:</strong> {account_tier} | <strong>Industry:</strong> {hs_industry}</p><p><strong>Plan Period:</strong> {plan_period} | <strong>Prepared for:</strong> {requestor_name}</p><hr/><h3>Executive Summary</h3><p>{plan_executive_summary}</p><h3>Financial Projections</h3><p>{financial_summary}</p><p><strong>Quarterly Targets:</strong> {quarterly_targets}</p><p><strong>Upsell Target:</strong> ${upsell_target} | <strong>Cross-Sell Target:</strong> ${cross_sell_target}</p><hr/><h3>SWOT Analysis</h3><p>{swot_analysis}</p><h3>Growth Strategies</h3><p>{growth_strategies}</p><h3>Expansion Opportunities</h3><p>{expansion_opportunities}</p><h3>Competitive Positioning</h3><p>{competitive_positioning}</p><h3>Risk Mitigation</h3><p>{risk_mitigation}</p><h3>Quarterly Milestones</h3><p>{quarterly_milestones}</p><h3>KPI Targets</h3><p>{kpi_targets}</p><h3>Resource Requirements</h3><p>{resource_requirements}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1515. APOLLO LEAD ENRICHMENT WORKFLOW (8 nodes)
  // ===================================================================

  "Apollo Lead Enrichment Workflow": {
    summary:
      "Receives new lead webhooks, extracts lead data with AI, enriches via Apollo API, validates enrichment quality with a condition gate, performs secondary enrichment via Clearbit for incomplete records, scores the lead with AI, updates the CRM record in HubSpot, and notifies the assigned SDR on Slack.",
    nodes: [
      trigger("Webhook: New lead captured", "webhook_app", {
        source: "lead_capture",
        event: "lead.created",
        howItStarts:
          "Fires when a new lead is captured from any source — form submission, chatbot conversation, LinkedIn integration, or manual entry — to enrich and score the lead before CRM routing",
        setup:
          "Configure webhook from your lead capture source. Set up Apollo API key for enrichment. Configure Clearbit API key for secondary enrichment. Set up OpenAI API key for lead scoring. Provide HubSpot API token. Set Slack webhook for SDR notifications.",
        inputFields: [],
      }),

      // 2. AI extract lead data
      aiNode(
        "2",
        "AI: Extract lead data from capture",
        280,
        "You are a lead data specialist. Extract the lead information from the raw webhook payload regardless of format. Find the person's email, first name, last name, company name, job title, phone number, lead source, and any form fields or UTM parameters. Respond JSON: {lead_email: string, first_name: string, last_name: string, company_name: string, job_title: string, phone: string, lead_source: string, utm_source: string, utm_campaign: string, form_data: string}",
        "Raw lead capture payload: {webhook_payload}",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "form_data", as: "form_data", type: "string" },
        ],
      ),

      // 3. Apollo enrichment API
      n("3", "hitApi", "Enrich lead via Apollo", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/people/match",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "Cache-Control", value: "no-cache" },
          ],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "email", value: "{lead_email}" },
            { key: "first_name", value: "{first_name}" },
            { key: "last_name", value: "{last_name}" },
            { key: "organization_name", value: "{company_name}" },
          ],
          variables: [
            {
              name: "person.title",
              customName: "apollo_title",
              type: "string",
            },
            {
              name: "person.seniority",
              customName: "apollo_seniority",
              type: "string",
            },
            {
              name: "person.departments",
              customName: "apollo_departments",
              type: "string",
            },
            {
              name: "person.linkedin_url",
              customName: "apollo_linkedin",
              type: "string",
            },
            {
              name: "person.organization.name",
              customName: "apollo_company",
              type: "string",
            },
            {
              name: "person.organization.estimated_num_employees",
              customName: "apollo_company_size",
              type: "number",
            },
            {
              name: "person.organization.industry",
              customName: "apollo_industry",
              type: "string",
            },
            {
              name: "person.organization.annual_revenue_printed",
              customName: "apollo_revenue",
              type: "string",
            },
            {
              name: "person.organization.website_url",
              customName: "apollo_website",
              type: "string",
            },
            {
              name: "person.state",
              customName: "apollo_state",
              type: "string",
            },
          ],
        },
      }),

      // 4. Data validation gate condition
      n("4", "condition", "Check: Apollo enrichment complete?", 760, {
        conditionData: [
          { variable: "apollo_title", operator: "is_not_empty", value: "" },
        ],
      }),

      // 5. Secondary enrichment API (False branch — incomplete Apollo data)
      n(
        "5",
        "hitApi",
        "Secondary enrichment via Clearbit",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://person.clearbit.com/v2/combined/find?email={lead_email}",
            headers: [
              { key: "Authorization", value: "Bearer {clearbit_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "person.employment.title",
                customName: "clearbit_title",
                type: "string",
              },
              {
                name: "person.employment.seniority",
                customName: "clearbit_seniority",
                type: "string",
              },
              {
                name: "company.name",
                customName: "clearbit_company",
                type: "string",
              },
              {
                name: "company.metrics.employeesRange",
                customName: "clearbit_company_size",
                type: "string",
              },
              {
                name: "company.category.industry",
                customName: "clearbit_industry",
                type: "string",
              },
              {
                name: "person.linkedin.handle",
                customName: "clearbit_linkedin",
                type: "string",
              },
            ],
          },
        },
        380,
      ),

      // 6. AI lead scorer
      aiNode(
        "6",
        "AI: Score and qualify lead",
        1240,
        "You are an expert lead scoring analyst. Score this lead on a 0-100 scale based on firmographic fit (company size, industry, revenue), demographic fit (title, seniority, department), behavioral signals (lead source, UTM campaign), and completeness of data. Classify as MQL, SQL, or Unqualified. Respond JSON: {lead_score: number, lead_grade: string, qualification: string, firmographic_score: number, demographic_score: number, behavioral_score: number, scoring_rationale: string, recommended_action: string, ideal_sdr: string, urgency: string}",
        "Lead: {first_name} {last_name} ({lead_email}). Title: {apollo_title} / {clearbit_title}. Seniority: {apollo_seniority} / {clearbit_seniority}. Company: {apollo_company} / {clearbit_company}. Size: {apollo_company_size} / {clearbit_company_size}. Industry: {apollo_industry} / {clearbit_industry}. Revenue: {apollo_revenue}. LinkedIn: {apollo_linkedin}. Source: {lead_source}. UTM: {utm_source}/{utm_campaign}. Departments: {apollo_departments}.",
        [
          { field: "lead_score", as: "lead_score", type: "number" },
          { field: "lead_grade", as: "lead_grade", type: "string" },
          { field: "qualification", as: "lead_qualification", type: "string" },
          {
            field: "firmographic_score",
            as: "firmographic_score",
            type: "number",
          },
          {
            field: "demographic_score",
            as: "demographic_score",
            type: "number",
          },
          { field: "behavioral_score", as: "behavioral_score", type: "number" },
          {
            field: "scoring_rationale",
            as: "scoring_rationale",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "ideal_sdr", as: "ideal_sdr", type: "string" },
          { field: "urgency", as: "lead_urgency", type: "string" },
        ],
      ),

      // 7. CRM record update HubSpot
      hubspotPostNode(
        "7",
        "Create/update lead in HubSpot",
        1480,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{apollo_company}","jobtitle":"{apollo_title}","phone":"{phone}","hs_lead_status":"{lead_qualification}","lead_score":"{lead_score}","industry":"{apollo_industry}","linkedin":"{apollo_linkedin}","utm_source":"{utm_source}","utm_campaign":"{utm_campaign}","notes":"Score: {lead_score} ({lead_grade}) | {lead_qualification} | Seniority: {apollo_seniority} | Revenue: {apollo_revenue} | Source: {lead_source}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hs_contact_email",
            type: "string",
          },
        ],
      ),

      // 8. SDR notification Slack
      slackNode(
        "8",
        "Notify assigned SDR (Slack)",
        1720,
        ":new: *New Lead Alert — {lead_qualification} ({lead_grade})*\n*Name:* {first_name} {last_name} | *Email:* {lead_email}\n*Title:* {apollo_title} ({apollo_seniority})\n*Company:* {apollo_company} | *Size:* {apollo_company_size} | *Industry:* {apollo_industry}\n*Revenue:* {apollo_revenue} | *LinkedIn:* {apollo_linkedin}\n\n*Score:* {lead_score}/100 | *Urgency:* {lead_urgency}\n*Firmographic:* {firmographic_score} | *Demographic:* {demographic_score} | *Behavioral:* {behavioral_score}\n*Rationale:* {scoring_rationale}\n*Source:* {lead_source} ({utm_source}/{utm_campaign})\n\n*Recommended Action:* {recommended_action}\n*Assigned SDR:* {ideal_sdr}\n*HubSpot ID:* {hubspot_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1516. AT-RISK ACCOUNT QBR INVITE (7 nodes)
  // ===================================================================

  "At-Risk Account QBR Invite": {
    summary:
      "Receives health score change webhooks, extracts account data with AI, pulls account risk details from HubSpot, builds a personalized QBR agenda with AI, schedules the QBR meeting via Calendly, sends the QBR invitation email to the customer, and logs the activity back to the CRM.",
    nodes: [
      trigger("Webhook: Account health score dropped", "webhook_app", {
        source: "customer_health",
        event: "account.health_score_changed",
        howItStarts:
          "Fires when a customer account's health score drops below a critical threshold, indicating churn risk and triggering an urgent QBR (Quarterly Business Review) invitation",
        setup:
          "Configure webhook from your customer health monitoring system (Gainsight, ChurnZero, or custom). Set up HubSpot API for account data. Configure OpenAI API key for agenda generation. Set Calendly API key. Configure email for QBR invitations.",
        inputFields: [],
      }),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI: Extract account health data",
        280,
        "You are a customer success analyst. Extract the account health change details from the raw webhook payload regardless of format. Find the company name, account ID, current health score, previous health score, health score change reason, primary contact name, primary contact email, CSM name, CSM email, account ARR, and renewal date. Respond JSON: {company_name: string, account_id: string, current_health_score: number, previous_health_score: number, score_change_reason: string, contact_name: string, contact_email: string, csm_name: string, csm_email: string, account_arr: number, renewal_date: string}",
        "Raw health score change payload: {webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          {
            field: "current_health_score",
            as: "current_health_score",
            type: "number",
          },
          {
            field: "previous_health_score",
            as: "previous_health_score",
            type: "number",
          },
          {
            field: "score_change_reason",
            as: "score_change_reason",
            type: "string",
          },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "csm_name", as: "csm_name", type: "string" },
          { field: "csm_email", as: "csm_email", type: "string" },
          { field: "account_arr", as: "account_arr", type: "number" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
        ],
      ),

      // 3. Account risk analyzer HubSpot
      hubspotGetNode(
        "3",
        "Load account risk details from HubSpot",
        520,
        "crm/v3/objects/companies/{account_id}?properties=name,lifecyclestage,num_associated_deals,notes_last_updated,hs_last_sales_activity_timestamp,hubspot_owner_id",
        [
          {
            name: "properties.lifecyclestage",
            customName: "hs_lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "hs_deal_count",
            type: "string",
          },
          {
            name: "properties.notes_last_updated",
            customName: "hs_last_note",
            type: "string",
          },
          {
            name: "properties.hs_last_sales_activity_timestamp",
            customName: "hs_last_activity",
            type: "string",
          },
        ],
      ),

      // 4. AI QBR agenda builder
      aiNode(
        "4",
        "AI: Build personalized QBR agenda",
        760,
        "You are a customer success leader specializing in at-risk account recovery. Build a personalized QBR agenda that addresses the specific reasons for the health score decline. Include value realization review, risk mitigation discussion, success plan realignment, product adoption analysis, and executive engagement. Make it focused on saving the account. Respond JSON: {agenda_summary: string, agenda_items: string, opening_statement: string, value_review_points: string, risk_discussion_topics: string, success_plan_updates: string, proposed_action_items: string, meeting_duration_minutes: number, escalation_recommendation: string}",
        "Account: {company_name}. Health: {current_health_score}/100 (was {previous_health_score}). Reason: {score_change_reason}. ARR: ${account_arr}. Renewal: {renewal_date}. Contact: {contact_name}. Lifecycle: {hs_lifecycle_stage}. Deals: {hs_deal_count}. Last activity: {hs_last_activity}.",
        [
          { field: "agenda_summary", as: "agenda_summary", type: "string" },
          { field: "agenda_items", as: "agenda_items", type: "string" },
          {
            field: "opening_statement",
            as: "opening_statement",
            type: "string",
          },
          {
            field: "value_review_points",
            as: "value_review_points",
            type: "string",
          },
          {
            field: "risk_discussion_topics",
            as: "risk_discussion_topics",
            type: "string",
          },
          {
            field: "success_plan_updates",
            as: "success_plan_updates",
            type: "string",
          },
          {
            field: "proposed_action_items",
            as: "proposed_action_items",
            type: "string",
          },
          {
            field: "meeting_duration_minutes",
            as: "meeting_duration_minutes",
            type: "number",
          },
          {
            field: "escalation_recommendation",
            as: "escalation_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. QBR calendar scheduler Calendly API
      n("5", "hitApi", "Schedule QBR via Calendly", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            { key: "owner", value: "{calendly_user_uri}" },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "calendly_booking_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "calendly_event_owner",
              type: "string",
            },
          ],
        },
      }),

      // 6. QBR invitation email
      emailNode(
        "6",
        "Send QBR invitation to customer",
        1240,
        "{contact_email}",
        "{contact_name}, let's align on {company_name}'s success — QBR invitation",
        "<p>Hi {contact_name},</p><p>{opening_statement}</p><p>I'd like to schedule a focused {meeting_duration_minutes}-minute Quarterly Business Review where we can:</p><p>{agenda_items}</p><p><strong>Key topics we'll cover:</strong></p><ul><li>Value realization: {value_review_points}</li><li>Success planning: {success_plan_updates}</li><li>Action items: {proposed_action_items}</li></ul><p><strong><a href='{calendly_booking_url}'>Click here to book a time that works for you</a></strong></p><p>Looking forward to connecting and ensuring we're delivering maximum value for {company_name}.</p><p>Best regards,<br/>{csm_name}</p>",
      ),

      // 7. CRM activity logger HubSpot
      hubspotPostNode(
        "7",
        "Log QBR invitation in HubSpot",
        1480,
        "/crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"QBR INVITATION SENT — At-Risk Account\\nHealth Score: {current_health_score}/100 (was {previous_health_score})\\nReason: {score_change_reason}\\nARR: ${account_arr} | Renewal: {renewal_date}\\nCalendly: {calendly_booking_url}\\nAgenda: {agenda_summary}\\nEscalation: {escalation_recommendation}","hs_timestamp":"{now}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{account_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":10}]}]',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1517. BETA FEEDBACK COLLECTION (8 nodes)
  // ===================================================================

  "Beta Feedback Collection": {
    summary:
      "Receives beta milestone webhooks, extracts feedback context with AI, loads user context from HubSpot, generates a personalized survey with AI, sends the feedback collection email, analyzes responses with AI, routes critical issues through a condition gate, and updates the beta dashboard in Sheets.",
    nodes: [
      trigger("Webhook: Beta milestone reached", "webhook_app", {
        source: "beta_platform",
        event: "beta.milestone_reached",
        howItStarts:
          "Fires when a beta user reaches a predefined milestone — e.g., 7 days active, first feature used, 10 actions completed — triggering a contextual feedback request",
        setup:
          "Configure webhook from your beta management system. Set up HubSpot API for user context. Configure OpenAI API key for survey generation and response analysis. Set up email for feedback requests. Configure Google Sheets for dashboard.",
        inputFields: [],
      }),

      // 2. AI extract feedback context
      aiNode(
        "2",
        "AI: Extract beta milestone context",
        280,
        "You are a product feedback specialist. Extract the beta milestone event details from the raw webhook payload regardless of format. Find the user email, user name, milestone type, days active, features used, actions completed, beta program name, user segment, and any error or friction events encountered. Respond JSON: {user_email: string, user_name: string, milestone_type: string, days_active: number, features_used: string, actions_completed: number, beta_program: string, user_segment: string, friction_events: string, beta_version: string}",
        "Raw beta milestone payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "days_active", as: "days_active", type: "number" },
          { field: "features_used", as: "features_used", type: "string" },
          {
            field: "actions_completed",
            as: "actions_completed",
            type: "number",
          },
          { field: "beta_program", as: "beta_program", type: "string" },
          { field: "user_segment", as: "user_segment", type: "string" },
          { field: "friction_events", as: "friction_events", type: "string" },
          { field: "beta_version", as: "beta_version", type: "string" },
        ],
      ),

      // 3. User context loader HubSpot
      hubspotGetNode(
        "3",
        "Load user context from HubSpot",
        520,
        "crm/v3/objects/contacts/search?query={user_email}&properties=email,firstname,lastname,company,jobtitle,lifecyclestage",
        [
          {
            name: "results[0].properties.company",
            customName: "hs_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "hs_job_title",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "hs_lifecycle",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. AI survey generator
      aiNode(
        "4",
        "AI: Generate personalized feedback survey",
        760,
        "You are a UX research specialist. Generate a personalized feedback survey tailored to the beta user's milestone and usage patterns. Create 4-5 targeted questions that address their specific experience. Include a mix of rating scales, multiple choice, and open-ended questions. Respond JSON: {survey_intro: string, question_1: string, question_1_type: string, question_2: string, question_2_type: string, question_3: string, question_3_type: string, question_4: string, question_4_type: string, survey_url: string, estimated_minutes: number}",
        "User: {user_name} ({user_email}). Company: {hs_company}. Title: {hs_job_title}. Milestone: {milestone_type}. Days active: {days_active}. Features used: {features_used}. Actions: {actions_completed}. Friction events: {friction_events}. Beta program: {beta_program}. Version: {beta_version}. Segment: {user_segment}.",
        [
          { field: "survey_intro", as: "survey_intro", type: "string" },
          { field: "question_1", as: "question_1", type: "string" },
          { field: "question_1_type", as: "question_1_type", type: "string" },
          { field: "question_2", as: "question_2", type: "string" },
          { field: "question_3", as: "question_3", type: "string" },
          { field: "question_4", as: "question_4", type: "string" },
          { field: "survey_url", as: "survey_url", type: "string" },
          { field: "estimated_minutes", as: "survey_minutes", type: "number" },
        ],
      ),

      // 5. Feedback collection email
      emailNode(
        "5",
        "Send personalized feedback request",
        1000,
        "{user_email}",
        "{user_name}, we'd love your feedback on {beta_program} ({survey_minutes} min)",
        "<p>Hi {user_name},</p><p>{survey_intro}</p><p>You've reached an exciting milestone: <strong>{milestone_type}</strong> after {days_active} days in the beta!</p><p>We've prepared a quick {survey_minutes}-minute survey based on your specific experience:</p><ol><li>{question_1} ({question_1_type})</li><li>{question_2}</li><li>{question_3}</li><li>{question_4}</li></ol><p><strong><a href='{survey_url}'>Take the Survey</a></strong></p><p>Your feedback directly shapes the final product. Thank you for being part of {beta_program}!</p>",
      ),

      // 6. AI response analyzer
      aiNode(
        "6",
        "AI: Analyze feedback responses",
        1240,
        "You are a product insights analyst. Analyze the beta feedback context and user behavior patterns to predict likely feedback themes and identify potential critical issues. Based on the friction events and usage patterns, determine the user's satisfaction level, likelihood to continue, and priority issues to address. Respond JSON: {predicted_satisfaction: number, continuation_likelihood: string, priority_issues: string, feature_sentiment: string, is_critical: boolean, recommended_followup: string, product_insight: string, improvement_category: string}",
        "User: {user_name} ({user_segment}). Milestone: {milestone_type}. Days active: {days_active}. Features used: {features_used}. Actions: {actions_completed}. Friction events: {friction_events}. Company: {hs_company}. Title: {hs_job_title}. Beta version: {beta_version}.",
        [
          {
            field: "predicted_satisfaction",
            as: "predicted_satisfaction",
            type: "number",
          },
          {
            field: "continuation_likelihood",
            as: "continuation_likelihood",
            type: "string",
          },
          { field: "priority_issues", as: "priority_issues", type: "string" },
          {
            field: "feature_sentiment",
            as: "feature_sentiment",
            type: "string",
          },
          { field: "is_critical", as: "is_critical_issue", type: "boolean" },
          {
            field: "recommended_followup",
            as: "recommended_followup",
            type: "string",
          },
          { field: "product_insight", as: "product_insight", type: "string" },
          {
            field: "improvement_category",
            as: "improvement_category",
            type: "string",
          },
        ],
      ),

      // 7. Critical issue router condition
      n("7", "condition", "Check: Has critical issues?", 1480, {
        conditionData: [
          { variable: "is_critical_issue", operator: "equals", value: "true" },
        ],
      }),

      // 8. Beta dashboard updater Sheets
      sheetsNode(
        "8",
        "Update beta feedback dashboard (Sheets)",
        1720,
        "Beta_Feedback",
        '[["{user_email}","{user_name}","{hs_company}","{beta_program}","{beta_version}","{milestone_type}","{days_active}","{actions_completed}","{features_used}","{friction_events}","{predicted_satisfaction}","{continuation_likelihood}","{priority_issues}","{is_critical_issue}","{improvement_category}","{product_insight}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1518. BETA SIGNUP ONBOARDING (8 nodes)
  // ===================================================================

  "Beta Signup Onboarding": {
    summary:
      "Receives beta signup webhooks, extracts signup data with AI, validates eligibility through a condition gate, sends a welcome email, provisions the beta account via API, delivers a setup guide email, waits 3 days, then sends an onboarding check-in email.",
    nodes: [
      trigger("Webhook: Beta signup submitted", "webhook_app", {
        source: "beta_signup",
        event: "beta.signup_submitted",
        howItStarts:
          "Fires when a new user signs up for a beta program via a landing page, waitlist form, or invitation link",
        setup:
          "Configure webhook from your beta signup form. Set up OpenAI API key for data extraction. Configure email for onboarding sequence. Set up account provisioning API endpoint.",
        inputFields: [],
      }),

      // 2. AI extract signup data
      aiNode(
        "2",
        "AI: Extract beta signup data",
        280,
        "You are a signup data processor. Extract the beta signup information from the raw webhook payload regardless of format. Find the email, name, company, role, use case interest, how they heard about the beta, operating system, and any referral code. Respond JSON: {signup_email: string, signup_name: string, company: string, role: string, use_case: string, referral_source: string, operating_system: string, referral_code: string, signup_timestamp: string, is_existing_customer: boolean}",
        "Raw beta signup payload: {webhook_payload}",
        [
          { field: "signup_email", as: "signup_email", type: "string" },
          { field: "signup_name", as: "signup_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "role", as: "role", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "operating_system", as: "operating_system", type: "string" },
          { field: "referral_code", as: "referral_code", type: "string" },
          { field: "signup_timestamp", as: "signup_timestamp", type: "string" },
          {
            field: "is_existing_customer",
            as: "is_existing_customer",
            type: "boolean",
          },
        ],
      ),

      // 3. Eligibility validator condition
      n("3", "condition", "Check: Eligible for beta?", 520, {
        conditionData: [
          { variable: "signup_email", operator: "is_not_empty", value: "" },
        ],
      }),

      // 4. Welcome email (True branch)
      emailNode(
        "4",
        "Send beta welcome email",
        760,
        "{signup_email}",
        "Welcome to the beta, {signup_name}! You're in.",
        "<h2>Welcome to the Beta Program!</h2><p>Hi {signup_name},</p><p>Great news — you've been accepted into our beta program! We're excited to have you on board.</p><p><strong>What happens next:</strong></p><ol><li>Your beta account is being provisioned now</li><li>You'll receive setup instructions within a few minutes</li><li>Our team is here to support you throughout the beta</li></ol><p><strong>Your beta details:</strong></p><ul><li><strong>Email:</strong> {signup_email}</li><li><strong>Company:</strong> {company}</li><li><strong>Use case:</strong> {use_case}</li><li><strong>Platform:</strong> {operating_system}</li></ul><p>Keep an eye on your inbox for your setup guide!</p>",
      ),

      // 5. Account provisioning API
      n("5", "hitApi", "Provision beta account", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourapp.com/v1/beta/accounts",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email", value: "{signup_email}" },
            { key: "name", value: "{signup_name}" },
            { key: "company", value: "{company}" },
            { key: "role", value: "{role}" },
            { key: "use_case", value: "{use_case}" },
            { key: "os", value: "{operating_system}" },
            { key: "referral_code", value: "{referral_code}" },
          ],
          variables: [
            {
              name: "account_id",
              customName: "beta_account_id",
              type: "string",
            },
            {
              name: "access_token",
              customName: "beta_access_token",
              type: "string",
            },
            {
              name: "dashboard_url",
              customName: "beta_dashboard_url",
              type: "string",
            },
            { name: "api_key", customName: "beta_api_key", type: "string" },
          ],
        },
      }),

      // 6. Setup guide delivery email
      emailNode(
        "6",
        "Send beta setup guide",
        1240,
        "{signup_email}",
        "Your beta account is ready — Setup guide for {signup_name}",
        "<h2>Your Beta Account is Ready!</h2><p>Hi {signup_name},</p><p>Your beta account has been provisioned and is ready to use. Here's everything you need to get started:</p><p><strong>Account Details:</strong></p><ul><li><strong>Account ID:</strong> {beta_account_id}</li><li><strong>Dashboard:</strong> <a href='{beta_dashboard_url}'>{beta_dashboard_url}</a></li></ul><p><strong>Quick Start Guide for {operating_system}:</strong></p><ol><li>Visit your <a href='{beta_dashboard_url}'>dashboard</a> and log in with {signup_email}</li><li>Complete the initial setup wizard</li><li>Try the feature most relevant to your use case: {use_case}</li><li>Share feedback using the in-app feedback widget</li></ol><p><strong>Need help?</strong> Reply to this email or join our beta Slack community.</p><p>We can't wait to hear what you think!</p>",
      ),

      // 7. Delay 3 days
      n("7", "delayAction", "Wait 3 days for initial usage", 1480, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 8. Onboarding check-in email
      emailNode(
        "8",
        "Send onboarding check-in email",
        1720,
        "{signup_email}",
        "How's it going, {signup_name}? Quick beta check-in",
        "<p>Hi {signup_name},</p><p>It's been a few days since you joined the beta — how's everything going?</p><p>I wanted to check in and see:</p><ul><li>Were you able to set up your account successfully?</li><li>Have you had a chance to explore {use_case}?</li><li>Is there anything confusing or not working as expected?</li></ul><p>Your feedback at this early stage is incredibly valuable. Even quick thoughts help us improve.</p><p><strong><a href='{beta_dashboard_url}'>Go to your dashboard</a></strong></p><p>Just reply to this email with any thoughts, questions, or issues. We read every response!</p><p>Thanks for being part of the beta,<br/>The Product Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1519. BLOG SUBSCRIBER TO MQL NURTURE (9 nodes)
  // ===================================================================

  "Blog Subscriber to MQL Nurture": {
    summary:
      "Receives blog subscription webhooks, extracts subscriber data with AI, enriches via Clearbit, scores ICP fit in code, selects personalized content with AI, sends an awareness-stage welcome email, waits 5 days, sends an interest-stage email with deeper content, and evaluates MQL readiness through a condition gate.",
    nodes: [
      trigger("Webhook: New blog subscriber", "webhook_app", {
        source: "blog_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when someone subscribes to the company blog, newsletter, or content hub — beginning a content-driven nurture sequence toward MQL qualification",
        setup:
          "Configure webhook from your blog/CMS platform (WordPress, Ghost, Webflow, etc.). Set up Clearbit API key for enrichment. Configure OpenAI API key for content selection. Set up email for nurture sequence.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI: Extract subscriber data",
        280,
        "You are a subscriber data specialist. Extract the blog subscriber information from the raw webhook payload regardless of format. Find the email, name, subscribed topics or categories, subscription source page URL, referrer, and any preference data. Respond JSON: {subscriber_email: string, subscriber_name: string, subscribed_topics: string, source_page_url: string, referrer: string, subscription_type: string, preferred_frequency: string, company_domain: string}",
        "Raw blog subscription payload: {webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "subscribed_topics",
            as: "subscribed_topics",
            type: "string",
          },
          { field: "source_page_url", as: "source_page_url", type: "string" },
          { field: "referrer", as: "referrer", type: "string" },
          {
            field: "subscription_type",
            as: "subscription_type",
            type: "string",
          },
          {
            field: "preferred_frequency",
            as: "preferred_frequency",
            type: "string",
          },
          { field: "company_domain", as: "company_domain", type: "string" },
        ],
      ),

      // 3. Subscriber enrichment Clearbit API
      n("3", "hitApi", "Enrich subscriber via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={subscriber_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "person.employment.title",
              customName: "cb_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "cb_seniority",
              type: "string",
            },
            {
              name: "person.employment.role",
              customName: "cb_role",
              type: "string",
            },
            { name: "company.name", customName: "cb_company", type: "string" },
            {
              name: "company.metrics.employeesRange",
              customName: "cb_company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "cb_industry",
              type: "string",
            },
            {
              name: "company.metrics.estimatedAnnualRevenue",
              customName: "cb_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. ICP fit scorer runCode
      n("4", "runCode", "Score ICP fit and segment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score subscriber against Ideal Customer Profile
const seniority = (vars.cb_seniority || "").toLowerCase();
const role = (vars.cb_role || "").toLowerCase();
const companySize = (vars.cb_company_size || "").toLowerCase();
const industry = (vars.cb_industry || "").toLowerCase();
let seniorityScore = 0;
if (seniority.includes("director") || seniority.includes("vp") || seniority.includes("c-suite")) seniorityScore = 30;
else if (seniority.includes("manager") || seniority.includes("senior")) seniorityScore = 20;
else if (seniority.includes("entry") || seniority.includes("individual")) seniorityScore = 5;
else seniorityScore = 10;
let roleScore = 0;
if (role.includes("marketing") || role.includes("sales") || role.includes("growth")) roleScore = 25;
else if (role.includes("product") || role.includes("engineering")) roleScore = 20;
else roleScore = 10;
let sizeScore = 0;
if (companySize.includes("51-200") || companySize.includes("201-500") || companySize.includes("501-1000")) sizeScore = 25;
else if (companySize.includes("1001") || companySize.includes("5001")) sizeScore = 20;
else sizeScore = 10;
let industryScore = 0;
if (industry.includes("software") || industry.includes("technology") || industry.includes("saas")) industryScore = 20;
else if (industry.includes("finance") || industry.includes("healthcare")) industryScore = 15;
else industryScore = 5;
const icpScore = seniorityScore + roleScore + sizeScore + industryScore;
const segment = icpScore >= 70 ? "HIGH_FIT" : icpScore >= 40 ? "MEDIUM_FIT" : "LOW_FIT";
const nurturePriority = icpScore >= 70 ? "PRIORITY" : "STANDARD";
return {
  icp_score: icpScore,
  icp_segment: segment,
  nurture_priority: nurturePriority,
  score_breakdown: "Seniority: " + seniorityScore + " + Role: " + roleScore + " + Size: " + sizeScore + " + Industry: " + industryScore,
};`,
          outputs: [
            { name: "icp_score", type: "number" },
            { name: "icp_segment", type: "string" },
            { name: "nurture_priority", type: "string" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 5. AI content selector
      aiNode(
        "5",
        "AI: Select personalized content path",
        1000,
        "You are a content marketing strategist. Based on the subscriber's profile, interests, and ICP fit, select the most appropriate awareness-stage and interest-stage content to nurture them toward MQL status. Choose specific blog posts, guides, case studies, or webinars from your content library. Respond JSON: {awareness_content_title: string, awareness_content_url: string, awareness_content_hook: string, interest_content_title: string, interest_content_url: string, interest_content_hook: string, personalized_greeting: string, content_theme: string}",
        "Subscriber: {subscriber_name} ({subscriber_email}). Topics: {subscribed_topics}. Source: {source_page_url}. Title: {cb_title} ({cb_seniority}). Role: {cb_role}. Company: {cb_company} ({cb_company_size}). Industry: {cb_industry}. ICP: {icp_segment} (score: {icp_score}). Priority: {nurture_priority}.",
        [
          {
            field: "awareness_content_title",
            as: "awareness_content_title",
            type: "string",
          },
          {
            field: "awareness_content_url",
            as: "awareness_content_url",
            type: "string",
          },
          {
            field: "awareness_content_hook",
            as: "awareness_content_hook",
            type: "string",
          },
          {
            field: "interest_content_title",
            as: "interest_content_title",
            type: "string",
          },
          {
            field: "interest_content_url",
            as: "interest_content_url",
            type: "string",
          },
          {
            field: "interest_content_hook",
            as: "interest_content_hook",
            type: "string",
          },
          {
            field: "personalized_greeting",
            as: "personalized_greeting",
            type: "string",
          },
          { field: "content_theme", as: "content_theme", type: "string" },
        ],
      ),

      // 6. Awareness stage email
      emailNode(
        "6",
        "Send awareness-stage welcome email",
        1240,
        "{subscriber_email}",
        "Welcome, {subscriber_name} — Here's your first read on {content_theme}",
        "<p>{personalized_greeting}</p><p>Thanks for subscribing! Based on your interest in {subscribed_topics}, we think you'll find this especially valuable:</p><h3>{awareness_content_title}</h3><p>{awareness_content_hook}</p><p><strong><a href='{awareness_content_url}'>Read Now</a></strong></p><p>We'll send you more insights on {content_theme} tailored to {cb_industry} professionals like you.</p><p>Happy reading!</p>",
      ),

      // 7. Delay 5 days
      n("7", "delayAction", "Wait 5 days for engagement", 1480, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 8. Interest stage email
      emailNode(
        "8",
        "Send interest-stage deeper content email",
        1720,
        "{subscriber_email}",
        "{subscriber_name}, ready to go deeper on {content_theme}?",
        "<p>Hi {subscriber_name},</p><p>Hope you enjoyed our previous piece on {awareness_content_title}. Ready to take the next step?</p><h3>{interest_content_title}</h3><p>{interest_content_hook}</p><p><strong><a href='{interest_content_url}'>Explore Now</a></strong></p><p>This guide is especially relevant for {cb_role} professionals at {cb_company_size} companies in {cb_industry}.</p><p>Want to see how other companies in your space are applying these ideas? <strong><a href='{interest_content_url}'>Check it out</a></strong></p>",
      ),

      // 9. MQL evaluator condition
      n("9", "condition", "Check: Ready for MQL qualification?", 1960, {
        conditionData: [
          {
            variable: "nurture_priority",
            operator: "equals",
            value: "PRIORITY",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1520. BUYING COMMITTEE MAPPER (8 nodes)
  // ===================================================================

  "Buying Committee Mapper": {
    summary:
      "Receives deal stage trigger webhooks, extracts deal data with AI, analyzes known contacts from HubSpot, classifies contact roles with AI, detects gaps in committee coverage in code, discovers additional contacts via API, generates an engagement strategy with AI, and updates the committee map in HubSpot.",
    nodes: [
      trigger("Webhook: Deal entered discovery stage", "webhook_app", {
        source: "crm_pipeline",
        event: "deal.stage_entered",
        howItStarts:
          "Fires when a deal enters a stage where buying committee mapping is critical — e.g., discovery, proposal, or negotiation — to identify and map all decision makers",
        setup:
          "Configure HubSpot webhook for deal stage events. Set up OpenAI API key for role classification and strategy generation. Provide contact discovery API key (Apollo, ZoomInfo). Configure HubSpot API for CRM updates.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI: Extract deal and opportunity data",
        280,
        "You are a sales intelligence specialist. Extract the deal information from the raw webhook payload regardless of format. Find the deal name, deal ID, company name, company ID, deal amount, deal stage, known contacts, industry, and deal owner. Respond JSON: {deal_name: string, deal_id: string, company_name: string, company_id: string, deal_amount: number, deal_stage: string, known_contact_emails: string, industry: string, deal_owner: string, deal_owner_email: string, product_interest: string}",
        "Raw deal stage trigger payload: {webhook_payload}",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_id", as: "company_id", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          {
            field: "known_contact_emails",
            as: "known_contact_emails",
            type: "string",
          },
          { field: "industry", as: "industry", type: "string" },
          { field: "deal_owner", as: "deal_owner", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
        ],
      ),

      // 3. Known contact analyzer HubSpot
      hubspotGetNode(
        "3",
        "Load known contacts from HubSpot",
        520,
        "crm/v3/objects/companies/{company_id}/associations/contacts?limit=20",
        [
          { name: "results[0].id", customName: "contact_1_id", type: "string" },
          { name: "results[1].id", customName: "contact_2_id", type: "string" },
          { name: "results[2].id", customName: "contact_3_id", type: "string" },
          {
            name: "results.length",
            customName: "known_contacts_count",
            type: "number",
          },
        ],
      ),

      // 4. AI role classifier
      aiNode(
        "4",
        "AI: Classify contact roles in buying committee",
        760,
        "You are a B2B sales strategist specializing in buying committee analysis. Based on the deal context, company size, and industry, classify the known contacts into buying committee roles (Champion, Economic Buyer, Technical Evaluator, End User, Influencer, Blocker, Legal/Procurement). Also identify which critical roles are missing from the known contacts. Respond JSON: {role_assignments: string, champion_identified: boolean, economic_buyer_identified: boolean, technical_evaluator_identified: boolean, missing_roles: string, committee_size_estimate: number, power_dynamics: string, engagement_risk: string, role_mapping_confidence: number}",
        "Deal: {deal_name} ($${deal_amount}) at {company_name}. Stage: {deal_stage}. Industry: {industry}. Product: {product_interest}. Known contacts: {known_contact_emails}. Contact count: {known_contacts_count}. Contact IDs: {contact_1_id}, {contact_2_id}, {contact_3_id}.",
        [
          { field: "role_assignments", as: "role_assignments", type: "string" },
          {
            field: "champion_identified",
            as: "champion_identified",
            type: "boolean",
          },
          {
            field: "economic_buyer_identified",
            as: "economic_buyer_identified",
            type: "boolean",
          },
          {
            field: "technical_evaluator_identified",
            as: "technical_evaluator_identified",
            type: "boolean",
          },
          { field: "missing_roles", as: "missing_roles", type: "string" },
          {
            field: "committee_size_estimate",
            as: "committee_size_estimate",
            type: "number",
          },
          { field: "power_dynamics", as: "power_dynamics", type: "string" },
          { field: "engagement_risk", as: "engagement_risk", type: "string" },
          {
            field: "role_mapping_confidence",
            as: "role_mapping_confidence",
            type: "number",
          },
        ],
      ),

      // 5. Gap detector runCode
      n("5", "runCode", "Detect committee coverage gaps", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect gaps in buying committee coverage
const knownCount = parseInt(vars.known_contacts_count || "0", 10);
const estimatedSize = parseInt(vars.committee_size_estimate || "5", 10);
const hasChampion = vars.champion_identified === "true" || vars.champion_identified === true;
const hasEconBuyer = vars.economic_buyer_identified === "true" || vars.economic_buyer_identified === true;
const hasTechEval = vars.technical_evaluator_identified === "true" || vars.technical_evaluator_identified === true;
const coveragePercent = Math.min(100, Math.round((knownCount / estimatedSize) * 100));
const criticalGaps = [];
if (!hasChampion) criticalGaps.push("Champion");
if (!hasEconBuyer) criticalGaps.push("Economic Buyer");
if (!hasTechEval) criticalGaps.push("Technical Evaluator");
const gapSeverity = criticalGaps.length >= 2 ? "HIGH" : criticalGaps.length === 1 ? "MEDIUM" : "LOW";
const needsDiscovery = coveragePercent < 60 || criticalGaps.length > 0;
const missingCount = Math.max(0, estimatedSize - knownCount);
return {
  coverage_percent: coveragePercent,
  critical_gaps: criticalGaps.join(", ") || "None",
  gap_severity: gapSeverity,
  needs_contact_discovery: needsDiscovery,
  missing_contacts_count: missingCount,
  committee_health: coveragePercent >= 80 && criticalGaps.length === 0 ? "HEALTHY" : coveragePercent >= 50 ? "PARTIAL" : "AT_RISK",
};`,
          outputs: [
            { name: "coverage_percent", type: "number" },
            { name: "critical_gaps", type: "string" },
            { name: "gap_severity", type: "string" },
            { name: "needs_contact_discovery", type: "boolean" },
            { name: "missing_contacts_count", type: "number" },
            { name: "committee_health", type: "string" },
          ],
        },
      }),

      // 6. Contact discovery API
      n("6", "hitApi", "Discover additional contacts via Apollo", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{apollo_api_key}" },
            { key: "q_organization_name", value: "{company_name}" },
            { key: "page", value: "1" },
            { key: "per_page", value: "10" },
            {
              key: "person_seniorities",
              value: '["director","vp","c_suite","manager"]',
            },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "discovered_contact_1_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "discovered_contact_1_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "discovered_contact_1_email",
              type: "string",
            },
            {
              name: "people[1].name",
              customName: "discovered_contact_2_name",
              type: "string",
            },
            {
              name: "people[1].title",
              customName: "discovered_contact_2_title",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 7. AI engagement strategy
      aiNode(
        "7",
        "AI: Generate committee engagement strategy",
        1480,
        "You are a strategic sales coach. Based on the complete buying committee map (known + discovered contacts), generate a multi-threaded engagement strategy. For each role, recommend a specific approach, messaging angle, and sequence of touchpoints. Respond JSON: {overall_strategy: string, champion_approach: string, economic_buyer_approach: string, technical_approach: string, threading_plan: string, risk_mitigation: string, timeline_recommendation: string, win_probability_estimate: number}",
        "Deal: {deal_name} ($${deal_amount}) at {company_name}. Stage: {deal_stage}. Industry: {industry}. Role assignments: {role_assignments}. Missing roles: {missing_roles}. Coverage: {coverage_percent}%. Committee health: {committee_health}. Gaps: {critical_gaps}. Discovered: {discovered_contact_1_name} ({discovered_contact_1_title}), {discovered_contact_2_name} ({discovered_contact_2_title}). Power dynamics: {power_dynamics}. Risk: {engagement_risk}.",
        [
          { field: "overall_strategy", as: "overall_strategy", type: "string" },
          {
            field: "champion_approach",
            as: "champion_approach",
            type: "string",
          },
          {
            field: "economic_buyer_approach",
            as: "economic_buyer_approach",
            type: "string",
          },
          {
            field: "technical_approach",
            as: "technical_approach",
            type: "string",
          },
          { field: "threading_plan", as: "threading_plan", type: "string" },
          { field: "risk_mitigation", as: "risk_mitigation", type: "string" },
          {
            field: "timeline_recommendation",
            as: "timeline_recommendation",
            type: "string",
          },
          {
            field: "win_probability_estimate",
            as: "win_probability",
            type: "number",
          },
        ],
      ),

      // 8. Committee map CRM update HubSpot
      hubspotPostNode(
        "8",
        "Update committee map in HubSpot",
        1720,
        "/crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes":"BUYING COMMITTEE MAP\\nCoverage: {coverage_percent}% ({committee_health})\\nRoles: {role_assignments}\\nGaps: {critical_gaps} ({gap_severity})\\nDiscovered: {discovered_contact_1_name} ({discovered_contact_1_title}), {discovered_contact_2_name} ({discovered_contact_2_title})\\n\\nSTRATEGY\\n{overall_strategy}\\nChampion: {champion_approach}\\nEcon Buyer: {economic_buyer_approach}\\nTechnical: {technical_approach}\\nWin Probability: {win_probability}%\\nTimeline: {timeline_recommendation}"}',
          },
        ],
        [{ name: "id", customName: "updated_deal_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1521. BUYING-COMMITTEE MULTI-CONTACT SYNC (8 nodes)
  // ===================================================================

  "Buying-Committee Multi-Contact Sync": {
    summary:
      "Receives buying committee activity webhooks, extracts activity data with AI, loads committee context from HubSpot, analyzes activity impact across contacts in code, coordinates cross-contact engagement in code, generates role-appropriate messages with AI, executes multi-channel outreach via email, and tracks deal momentum in Sheets.",
    nodes: [
      trigger("Webhook: Committee member activity detected", "webhook_app", {
        source: "engagement_tracker",
        event: "committee.activity_detected",
        howItStarts:
          "Fires when any buying committee member performs a tracked activity — website visit, email open, content download, pricing page view, or competitor research signal",
        setup:
          "Configure webhook from your engagement tracking platform. Set up HubSpot API for committee context. Configure OpenAI API key for message generation. Set up email for outreach. Configure Google Sheets for momentum tracking.",
        inputFields: [],
      }),

      // 2. AI extract activity data
      aiNode(
        "2",
        "AI: Extract committee activity data",
        280,
        "You are a sales engagement analyst. Extract the committee member activity details from the raw webhook payload regardless of format. Find the contact email, contact name, activity type, activity timestamp, page or content viewed, duration, deal ID, company name, and engagement score change. Respond JSON: {contact_email: string, contact_name: string, activity_type: string, activity_timestamp: string, content_viewed: string, duration_seconds: number, deal_id: string, company_name: string, engagement_delta: number, contact_role: string}",
        "Raw committee activity payload: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "activity_type", as: "activity_type", type: "string" },
          {
            field: "activity_timestamp",
            as: "activity_timestamp",
            type: "string",
          },
          { field: "content_viewed", as: "content_viewed", type: "string" },
          { field: "duration_seconds", as: "duration_seconds", type: "number" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "engagement_delta", as: "engagement_delta", type: "number" },
          { field: "contact_role", as: "contact_role", type: "string" },
        ],
      ),

      // 3. Committee context loader HubSpot
      hubspotGetNode(
        "3",
        "Load committee context from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?associations=contacts&properties=dealname,amount,dealstage,hubspot_owner_id,notes_last_updated",
        [
          {
            name: "properties.dealname",
            customName: "hs_deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "hs_deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "hs_deal_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "hs_deal_owner",
            type: "string",
          },
          {
            name: "associations.contacts.results.length",
            customName: "committee_size",
            type: "number",
          },
        ],
      ),

      // 4. Activity impact analyzer runCode
      n("4", "runCode", "Analyze activity impact across committee", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze the impact of this activity in the context of the buying committee
const activityType = vars.activity_type || "";
const duration = parseInt(vars.duration_seconds || "0", 10);
const engagementDelta = parseInt(vars.engagement_delta || "0", 10);
const contactRole = (vars.contact_role || "unknown").toLowerCase();
let activityWeight = 1;
if (activityType.includes("pricing")) activityWeight = 5;
else if (activityType.includes("demo") || activityType.includes("trial")) activityWeight = 4;
else if (activityType.includes("case_study") || activityType.includes("comparison")) activityWeight = 3;
else if (activityType.includes("download") || activityType.includes("webinar")) activityWeight = 2;
let roleMultiplier = 1;
if (contactRole.includes("economic") || contactRole.includes("decision")) roleMultiplier = 2;
else if (contactRole.includes("champion")) roleMultiplier = 1.5;
else if (contactRole.includes("technical")) roleMultiplier = 1.3;
const impactScore = Math.min(100, Math.round(activityWeight * roleMultiplier * 10 + engagementDelta));
const signalStrength = impactScore >= 70 ? "STRONG_BUY_SIGNAL" : impactScore >= 40 ? "ACTIVE_EVALUATION" : "CASUAL_INTEREST";
const durationLabel = duration >= 300 ? "Deep engagement (" + Math.round(duration / 60) + " min)" : duration >= 60 ? "Moderate (" + Math.round(duration / 60) + " min)" : "Brief";
return {
  activity_impact_score: impactScore,
  signal_strength: signalStrength,
  duration_label: durationLabel,
  activity_weight: activityWeight,
  role_multiplier: roleMultiplier,
  requires_immediate_response: impactScore >= 60,
};`,
          outputs: [
            { name: "activity_impact_score", type: "number" },
            { name: "signal_strength", type: "string" },
            { name: "duration_label", type: "string" },
            { name: "activity_weight", type: "number" },
            { name: "role_multiplier", type: "number" },
            { name: "requires_immediate_response", type: "boolean" },
          ],
        },
      }),

      // 5. Cross-contact coordination engine runCode
      n("5", "runCode", "Coordinate cross-contact engagement plan", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Coordinate engagement across the buying committee
const signalStrength = vars.signal_strength || "CASUAL_INTEREST";
const contactRole = vars.contact_role || "unknown";
const activityType = vars.activity_type || "";
const committeeSize = parseInt(vars.committee_size || "1", 10);
const dealAmount = parseInt(vars.hs_deal_amount || "0", 10);
let engagementPlan = "";
let otherContactAction = "";
if (signalStrength === "STRONG_BUY_SIGNAL") {
  engagementPlan = "Immediate personalized follow-up to active contact + proactive outreach to economic buyer";
  otherContactAction = "Send ROI summary to economic buyer, technical deep-dive to evaluator";
} else if (signalStrength === "ACTIVE_EVALUATION") {
  engagementPlan = "Same-day follow-up with relevant content + check-in with champion";
  otherContactAction = "Share activity update with champion for internal advocacy";
} else {
  engagementPlan = "Queue for next scheduled touchpoint";
  otherContactAction = "No additional outreach needed at this time";
}
const syncId = "SYNC-" + Date.now().toString(36).toUpperCase();
return {
  sync_id: syncId,
  engagement_plan: engagementPlan,
  other_contact_action: otherContactAction,
  coordination_timestamp: new Date().toISOString(),
  active_contact_priority: signalStrength === "STRONG_BUY_SIGNAL" ? "IMMEDIATE" : "STANDARD",
};`,
          outputs: [
            { name: "sync_id", type: "string" },
            { name: "engagement_plan", type: "string" },
            { name: "other_contact_action", type: "string" },
            { name: "coordination_timestamp", type: "string" },
            { name: "active_contact_priority", type: "string" },
          ],
        },
      }),

      // 6. AI role-appropriate message generator
      aiNode(
        "6",
        "AI: Generate role-appropriate follow-up message",
        1240,
        "You are a B2B sales communication expert. Generate a personalized follow-up email for the active committee member based on their role, the specific activity they performed, and the content they viewed. The message should feel natural, not automated, and reference their specific interest. Respond JSON: {email_subject: string, email_body: string, cta_text: string, cta_url: string, tone: string, follow_up_timing: string}",
        "Contact: {contact_name} ({contact_email}), Role: {contact_role}. Activity: {activity_type} — viewed {content_viewed} for {duration_label}. Signal: {signal_strength}. Deal: {hs_deal_name} (${hs_deal_amount}), Stage: {hs_deal_stage}. Company: {company_name}. Impact: {activity_impact_score}/100. Plan: {engagement_plan}.",
        [
          { field: "email_subject", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          { field: "cta_text", as: "followup_cta", type: "string" },
          { field: "cta_url", as: "followup_cta_url", type: "string" },
          { field: "tone", as: "message_tone", type: "string" },
          { field: "follow_up_timing", as: "followup_timing", type: "string" },
        ],
      ),

      // 7. Multi-channel outreach executor email
      emailNode(
        "7",
        "Send personalized follow-up email",
        1480,
        "{contact_email}",
        "{followup_subject}",
        "{followup_body}<p><strong><a href='{followup_cta_url}'>{followup_cta}</a></strong></p>",
      ),

      // 8. Deal momentum scorer Sheets
      sheetsNode(
        "8",
        "Log deal momentum data (Sheets)",
        1720,
        "Deal_Momentum",
        '[["{sync_id}","{coordination_timestamp}","{company_name}","{hs_deal_name}","{hs_deal_amount}","{hs_deal_stage}","{contact_name}","{contact_role}","{activity_type}","{content_viewed}","{activity_impact_score}","{signal_strength}","{active_contact_priority}","{engagement_plan}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1522. BUYING-COMMITTEE NURTURE AT ACCOUNT LEVEL (9 nodes)
  // ===================================================================

  "Buying-Committee Nurture at Account Level": {
    summary:
      "Receives account nurture trigger webhooks, extracts account data with AI, loads the full committee from HubSpot, builds a unified account narrative with AI, maps role-specific content in code, schedules coordinated cadences in code, sends executive sponsor emails, sends technical evaluator emails, and aggregates engagement data in Sheets.",
    nodes: [
      trigger("Webhook: Account nurture cycle triggered", "webhook_app", {
        source: "nurture_orchestrator",
        event: "account.nurture_triggered",
        howItStarts:
          "Fires when an account enters a nurture cycle — typically after initial engagement stalls, a deal goes dormant, or a renewal window opens — to coordinate outreach across the entire buying committee",
        setup:
          "Configure webhook from your nurture orchestration system. Set up HubSpot API for committee data. Configure OpenAI API key for narrative and content generation. Set up email for multi-role outreach. Configure Google Sheets for engagement tracking.",
        inputFields: [],
      }),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI: Extract account nurture trigger data",
        280,
        "You are an account-based marketing specialist. Extract the account nurture trigger details from the raw webhook payload regardless of format. Find the company name, account ID, nurture reason, account tier, current deal stage, total deal value, executive sponsor contact, technical evaluator contact, and the nurture campaign type. Respond JSON: {company_name: string, account_id: string, nurture_reason: string, account_tier: string, deal_stage: string, total_deal_value: number, exec_sponsor_email: string, exec_sponsor_name: string, tech_evaluator_email: string, tech_evaluator_name: string, campaign_type: string}",
        "Raw account nurture trigger: {webhook_payload}",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "nurture_reason", as: "nurture_reason", type: "string" },
          { field: "account_tier", as: "account_tier", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "total_deal_value", as: "total_deal_value", type: "number" },
          {
            field: "exec_sponsor_email",
            as: "exec_sponsor_email",
            type: "string",
          },
          {
            field: "exec_sponsor_name",
            as: "exec_sponsor_name",
            type: "string",
          },
          {
            field: "tech_evaluator_email",
            as: "tech_evaluator_email",
            type: "string",
          },
          {
            field: "tech_evaluator_name",
            as: "tech_evaluator_name",
            type: "string",
          },
          { field: "campaign_type", as: "campaign_type", type: "string" },
        ],
      ),

      // 3. Account committee loader HubSpot
      hubspotGetNode(
        "3",
        "Load buying committee from HubSpot",
        520,
        "crm/v3/objects/companies/{account_id}?associations=contacts,deals&properties=name,industry,annualrevenue,numberofemployees,notes_last_updated",
        [
          {
            name: "properties.industry",
            customName: "hs_industry",
            type: "string",
          },
          {
            name: "properties.annualrevenue",
            customName: "hs_revenue",
            type: "string",
          },
          {
            name: "properties.numberofemployees",
            customName: "hs_employees",
            type: "string",
          },
          {
            name: "properties.notes_last_updated",
            customName: "hs_last_note",
            type: "string",
          },
          {
            name: "associations.contacts.results.length",
            customName: "total_committee_contacts",
            type: "number",
          },
          {
            name: "associations.deals.results.length",
            customName: "total_deals",
            type: "number",
          },
        ],
      ),

      // 4. AI account narrative builder
      aiNode(
        "4",
        "AI: Build unified account narrative",
        760,
        "You are a strategic account marketing storyteller. Build a compelling, unified account narrative that will be adapted for different buying committee roles. The narrative should address the company's industry challenges, how your solution uniquely fits their needs, and create urgency around the nurture reason. Respond JSON: {core_narrative: string, executive_angle: string, technical_angle: string, value_proposition: string, urgency_message: string, industry_trend: string, competitive_differentiation: string, social_proof: string, roi_headline: string}",
        "Company: {company_name}. Industry: {hs_industry}. Revenue: {hs_revenue}. Employees: {hs_employees}. Nurture reason: {nurture_reason}. Deal stage: {deal_stage}. Deal value: ${total_deal_value}. Tier: {account_tier}. Campaign: {campaign_type}. Committee size: {total_committee_contacts}. Last activity: {hs_last_note}.",
        [
          { field: "core_narrative", as: "core_narrative", type: "string" },
          { field: "executive_angle", as: "executive_angle", type: "string" },
          { field: "technical_angle", as: "technical_angle", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "urgency_message", as: "urgency_message", type: "string" },
          { field: "industry_trend", as: "industry_trend", type: "string" },
          {
            field: "competitive_differentiation",
            as: "competitive_differentiation",
            type: "string",
          },
          { field: "social_proof", as: "social_proof", type: "string" },
          { field: "roi_headline", as: "roi_headline", type: "string" },
        ],
      ),

      // 5. Role-specific content mapper runCode
      n("5", "runCode", "Map content to committee roles", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map content and messaging to each committee role
const tier = (vars.account_tier || "standard").toUpperCase();
const dealValue = parseInt(vars.total_deal_value || "0", 10);
const execContent = {
  subject: "Strategic opportunity for " + vars.company_name + ": " + vars.roi_headline,
  contentType: dealValue >= 50000 ? "Executive Briefing + ROI Analysis" : "Industry Insights + Case Study",
  cta: dealValue >= 50000 ? "Schedule an executive strategy session" : "See how peers in " + vars.hs_industry + " are transforming",
};
const techContent = {
  subject: vars.company_name + " technical deep-dive: " + vars.competitive_differentiation,
  contentType: "Technical Architecture Guide + Integration Documentation",
  cta: "Access our technical sandbox and API documentation",
};
const cadenceId = "NRT-" + Date.now().toString(36).toUpperCase();
return {
  exec_subject: execContent.subject,
  exec_content_type: execContent.contentType,
  exec_cta: execContent.cta,
  tech_subject: techContent.subject,
  tech_content_type: techContent.contentType,
  tech_cta: techContent.cta,
  cadence_id: cadenceId,
  content_mapped_at: new Date().toISOString(),
};`,
          outputs: [
            { name: "exec_subject", type: "string" },
            { name: "exec_content_type", type: "string" },
            { name: "exec_cta", type: "string" },
            { name: "tech_subject", type: "string" },
            { name: "tech_content_type", type: "string" },
            { name: "tech_cta", type: "string" },
            { name: "cadence_id", type: "string" },
            { name: "content_mapped_at", type: "string" },
          ],
        },
      }),

      // 6. Coordinated cadence scheduler runCode
      n("6", "runCode", "Schedule coordinated outreach cadence", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Schedule the coordinated multi-role outreach cadence
const tier = (vars.account_tier || "standard").toUpperCase();
const committeeSize = parseInt(vars.total_committee_contacts || "2", 10);
const execSendDay = "Day 0 (Today)";
const techSendDay = "Day 1 (Tomorrow)";
const followUpDay = tier === "TIER_1" || tier === "ENTERPRISE" ? "Day 5" : "Day 7";
const cadenceDuration = tier === "TIER_1" || tier === "ENTERPRISE" ? 14 : 21;
const totalTouchpoints = committeeSize * 3;
return {
  exec_send_timing: execSendDay,
  tech_send_timing: techSendDay,
  follow_up_timing: followUpDay,
  cadence_duration_days: cadenceDuration,
  total_touchpoints: totalTouchpoints,
  cadence_status: "ACTIVE",
  coordination_note: "Exec email first, tech email 24hrs later, follow-up " + followUpDay,
};`,
          outputs: [
            { name: "exec_send_timing", type: "string" },
            { name: "tech_send_timing", type: "string" },
            { name: "follow_up_timing", type: "string" },
            { name: "cadence_duration_days", type: "number" },
            { name: "total_touchpoints", type: "number" },
            { name: "cadence_status", type: "string" },
            { name: "coordination_note", type: "string" },
          ],
        },
      }),

      // 7. Executive sponsor email
      emailNode(
        "7",
        "Send executive sponsor nurture email",
        1480,
        "{exec_sponsor_email}",
        "{exec_subject}",
        "<p>Hi {exec_sponsor_name},</p><p>{executive_angle}</p><p><strong>{industry_trend}</strong></p><p>{value_proposition}</p><p>{social_proof}</p><p>{urgency_message}</p><p><strong>{exec_cta}</strong></p><p>I'd welcome the opportunity to discuss how we can help {company_name} capitalize on this trend.</p>",
      ),

      // 8. Technical evaluator email
      emailNode(
        "8",
        "Send technical evaluator nurture email",
        1720,
        "{tech_evaluator_email}",
        "{tech_subject}",
        "<p>Hi {tech_evaluator_name},</p><p>{technical_angle}</p><p><strong>{competitive_differentiation}</strong></p><p>We've prepared a {tech_content_type} specifically for teams evaluating solutions in {hs_industry}.</p><p>{tech_cta}</p><p>Happy to answer any technical questions or set up a hands-on session.</p>",
      ),

      // 9. Account engagement aggregator Sheets
      sheetsNode(
        "9",
        "Log account nurture engagement (Sheets)",
        1960,
        "Account_Nurture",
        '[["{cadence_id}","{content_mapped_at}","{company_name}","{account_tier}","{hs_industry}","{nurture_reason}","{campaign_type}","{total_deal_value}","{deal_stage}","{total_committee_contacts}","{exec_sponsor_name}","{tech_evaluator_name}","{cadence_status}","{cadence_duration_days}","{total_touchpoints}","{coordination_note}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1523. CI/CD PIPELINE ALERT (7 nodes)
  // ===================================================================

  "CI/CD Pipeline Alert": {
    summary:
      "Receives pipeline event webhooks from CI/CD systems, extracts pipeline data with AI, normalizes event data in code, evaluates pass/fail status via condition, sends failure alerts to Slack, sends success notifications to Slack, and records pipeline metrics in Sheets.",
    nodes: [
      trigger("Webhook: CI/CD pipeline event", "webhook_app", {
        source: "cicd_system",
        event: "pipeline.event",
        howItStarts:
          "Fires when any CI/CD pipeline event occurs — build started, tests passed, deployment completed, or pipeline failed — from GitHub Actions, GitLab CI, Jenkins, or CircleCI",
        setup:
          "Configure webhook from your CI/CD system (GitHub Actions, GitLab CI, Jenkins, CircleCI). Set up OpenAI API key for event extraction. Configure Slack webhook for alerts. Set Google Sheets for metrics.",
        inputFields: [],
      }),

      // 2. AI extract pipeline data
      aiNode(
        "2",
        "AI: Extract pipeline event data",
        280,
        "You are a DevOps event parser. Extract the CI/CD pipeline event details from the raw webhook payload regardless of format. Find the pipeline name, pipeline ID, event type (build, test, deploy), status (success, failure, in_progress), branch, commit SHA, commit author, commit message, duration in seconds, and repository name. Respond JSON: {pipeline_name: string, pipeline_id: string, event_type: string, status: string, branch: string, commit_sha: string, commit_author: string, commit_message: string, duration_seconds: number, repository: string, environment: string}",
        "Raw CI/CD pipeline event: {webhook_payload}",
        [
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
          { field: "pipeline_id", as: "pipeline_id", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "status", as: "pipeline_status", type: "string" },
          { field: "branch", as: "branch", type: "string" },
          { field: "commit_sha", as: "commit_sha", type: "string" },
          { field: "commit_author", as: "commit_author", type: "string" },
          { field: "commit_message", as: "commit_message", type: "string" },
          { field: "duration_seconds", as: "duration_seconds", type: "number" },
          { field: "repository", as: "repository", type: "string" },
          { field: "environment", as: "environment", type: "string" },
        ],
      ),

      // 3. Event normalizer runCode
      n("3", "runCode", "Normalize pipeline event data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize pipeline event data for consistent processing
const status = (vars.pipeline_status || "").toLowerCase();
const eventType = (vars.event_type || "").toLowerCase();
const duration = parseInt(vars.duration_seconds || "0", 10);
const isSuccess = status === "success" || status === "passed" || status === "completed";
const isFailure = status === "failure" || status === "failed" || status === "error";
const normalizedStatus = isSuccess ? "SUCCESS" : isFailure ? "FAILURE" : "IN_PROGRESS";
const durationMinutes = Math.round(duration / 60);
const durationLabel = durationMinutes >= 60 ? Math.round(durationMinutes / 60) + "h " + (durationMinutes % 60) + "m" : durationMinutes + "m";
const isProduction = (vars.environment || "").toLowerCase().includes("prod") || (vars.branch || "").toLowerCase() === "main" || (vars.branch || "").toLowerCase() === "master";
const severity = isFailure && isProduction ? "CRITICAL" : isFailure ? "WARNING" : "INFO";
const shortSha = (vars.commit_sha || "").substring(0, 7);
return {
  normalized_status: normalizedStatus,
  is_success: isSuccess,
  is_failure: isFailure,
  duration_label: durationLabel,
  is_production: isProduction,
  severity: severity,
  short_sha: shortSha,
  event_timestamp: new Date().toISOString(),
};`,
          outputs: [
            { name: "normalized_status", type: "string" },
            { name: "is_success", type: "boolean" },
            { name: "is_failure", type: "boolean" },
            { name: "duration_label", type: "string" },
            { name: "is_production", type: "boolean" },
            { name: "severity", type: "string" },
            { name: "short_sha", type: "string" },
            { name: "event_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Status evaluator condition
      n("4", "condition", "Check: Pipeline failed?", 760, {
        conditionData: [
          { variable: "is_failure", operator: "equals", value: "true" },
        ],
      }),

      // 5. Build failure alert Slack (True branch)
      slackNode(
        "5",
        "Alert: Pipeline failure (Slack)",
        1000,
        ":red_circle: *Pipeline FAILURE — {severity}*\n*Pipeline:* {pipeline_name} ({pipeline_id})\n*Event:* {event_type} | *Status:* {normalized_status}\n*Repo:* {repository} | *Branch:* {branch} | *Env:* {environment}\n*Commit:* `{short_sha}` by {commit_author}\n*Message:* {commit_message}\n*Duration:* {duration_label}\n*Production:* {is_production}",
        -220,
      ),

      // 6. Deployment success notification Slack (False branch)
      slackNode(
        "6",
        "Notify: Pipeline success (Slack)",
        1000,
        ":large_green_circle: *Pipeline {normalized_status}*\n*Pipeline:* {pipeline_name} ({pipeline_id})\n*Event:* {event_type} | *Branch:* {branch} | *Env:* {environment}\n*Commit:* `{short_sha}` by {commit_author} — {commit_message}\n*Duration:* {duration_label}",
        380,
      ),

      // 7. Pipeline metrics recorder Sheets
      sheetsNode(
        "7",
        "Record pipeline metrics (Sheets)",
        1240,
        "Pipeline_Metrics",
        '[["{event_timestamp}","{pipeline_name}","{pipeline_id}","{event_type}","{normalized_status}","{repository}","{branch}","{environment}","{commit_author}","{short_sha}","{commit_message}","{duration_seconds}","{duration_label}","{severity}","{is_production}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1524. CI/CD PIPELINE FAILURE ALERT (8 nodes)
  // ===================================================================

  "CI/CD Pipeline Failure Alert": {
    summary:
      "Receives pipeline failure webhooks, extracts failure data with AI, parses failure logs in code, categorizes the failure type via condition, runs AI root cause analysis, notifies the commit author on Slack, alerts the team channel on Slack, and logs recovery data in Sheets.",
    nodes: [
      trigger("Webhook: CI/CD pipeline failure detected", "webhook_app", {
        source: "cicd_system",
        event: "pipeline.failed",
        howItStarts:
          "Fires specifically when a CI/CD pipeline fails — build errors, test failures, deployment rollbacks — triggering an intelligent failure analysis and notification workflow",
        setup:
          "Configure webhook from your CI/CD system for failure events. Set up OpenAI API key for root cause analysis. Configure Slack webhooks for author and team notifications. Set Google Sheets for failure tracking.",
        inputFields: [],
      }),

      // 2. AI extract failure data
      aiNode(
        "2",
        "AI: Extract pipeline failure data",
        280,
        "You are a DevOps failure analyst. Extract the CI/CD pipeline failure details from the raw webhook payload regardless of format. Find the pipeline name, failure stage, error message, error code, failing step, commit author, commit SHA, branch, repository, build number, and any log excerpt. Respond JSON: {pipeline_name: string, failure_stage: string, error_message: string, error_code: string, failing_step: string, commit_author: string, commit_author_slack: string, commit_sha: string, branch: string, repository: string, build_number: string, log_excerpt: string, environment: string}",
        "Raw pipeline failure payload: {webhook_payload}",
        [
          { field: "pipeline_name", as: "pipeline_name", type: "string" },
          { field: "failure_stage", as: "failure_stage", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
          { field: "error_code", as: "error_code", type: "string" },
          { field: "failing_step", as: "failing_step", type: "string" },
          { field: "commit_author", as: "commit_author", type: "string" },
          {
            field: "commit_author_slack",
            as: "commit_author_slack",
            type: "string",
          },
          { field: "commit_sha", as: "commit_sha", type: "string" },
          { field: "branch", as: "branch", type: "string" },
          { field: "repository", as: "repository", type: "string" },
          { field: "build_number", as: "build_number", type: "string" },
          { field: "log_excerpt", as: "log_excerpt", type: "string" },
          { field: "environment", as: "environment", type: "string" },
        ],
      ),

      // 3. Failure log parser runCode
      n("3", "runCode", "Parse and categorize failure logs", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse failure logs to extract structured failure information
const errorMsg = (vars.error_message || "").toLowerCase();
const failureStage = (vars.failure_stage || "").toLowerCase();
const logExcerpt = (vars.log_excerpt || "").toLowerCase();
const combined = errorMsg + " " + failureStage + " " + logExcerpt;
let failureCategory = "UNKNOWN";
if (combined.includes("test") || combined.includes("assert") || combined.includes("expect")) failureCategory = "TEST_FAILURE";
else if (combined.includes("build") || combined.includes("compile") || combined.includes("syntax")) failureCategory = "BUILD_ERROR";
else if (combined.includes("deploy") || combined.includes("rollback") || combined.includes("timeout")) failureCategory = "DEPLOYMENT_FAILURE";
else if (combined.includes("dependency") || combined.includes("npm") || combined.includes("package")) failureCategory = "DEPENDENCY_ERROR";
else if (combined.includes("permission") || combined.includes("auth") || combined.includes("credential")) failureCategory = "AUTH_ERROR";
else if (combined.includes("memory") || combined.includes("oom") || combined.includes("resource")) failureCategory = "RESOURCE_ERROR";
const isFlaky = combined.includes("flaky") || combined.includes("intermittent") || combined.includes("timeout");
const isProd = (vars.environment || "").toLowerCase().includes("prod");
const urgencyLevel = isProd ? "P1_CRITICAL" : failureCategory === "TEST_FAILURE" && isFlaky ? "P3_LOW" : "P2_STANDARD";
const shortSha = (vars.commit_sha || "").substring(0, 7);
return {
  failure_category: failureCategory,
  is_flaky: isFlaky,
  urgency_level: urgencyLevel,
  is_production: isProd,
  short_sha: shortSha,
  failure_timestamp: new Date().toISOString(),
  needs_root_cause: failureCategory !== "TEST_FAILURE" || !isFlaky,
};`,
          outputs: [
            { name: "failure_category", type: "string" },
            { name: "is_flaky", type: "boolean" },
            { name: "urgency_level", type: "string" },
            { name: "is_production", type: "boolean" },
            { name: "short_sha", type: "string" },
            { name: "failure_timestamp", type: "string" },
            { name: "needs_root_cause", type: "boolean" },
          ],
        },
      }),

      // 4. Failure categorizer condition
      n("4", "condition", "Check: Needs root cause analysis?", 760, {
        conditionData: [
          { variable: "needs_root_cause", operator: "equals", value: "true" },
        ],
      }),

      // 5. AI root cause analyzer (True branch)
      aiNode(
        "5",
        "AI: Analyze root cause of failure",
        1000,
        "You are a senior DevOps engineer specializing in CI/CD failure analysis. Analyze the pipeline failure and provide a root cause analysis, suggested fixes, and preventive measures. Be specific and actionable. Respond JSON: {root_cause: string, suggested_fix: string, fix_complexity: string, preventive_measures: string, similar_failure_pattern: string, estimated_fix_time: string, needs_escalation: boolean}",
        "Pipeline: {pipeline_name}. Failure stage: {failure_stage}. Category: {failure_category}. Error: {error_message}. Error code: {error_code}. Failing step: {failing_step}. Branch: {branch}. Repo: {repository}. Build: {build_number}. Environment: {environment}. Flaky: {is_flaky}. Log excerpt: {log_excerpt}.",
        [
          { field: "root_cause", as: "root_cause", type: "string" },
          { field: "suggested_fix", as: "suggested_fix", type: "string" },
          { field: "fix_complexity", as: "fix_complexity", type: "string" },
          {
            field: "preventive_measures",
            as: "preventive_measures",
            type: "string",
          },
          {
            field: "similar_failure_pattern",
            as: "similar_failure_pattern",
            type: "string",
          },
          {
            field: "estimated_fix_time",
            as: "estimated_fix_time",
            type: "string",
          },
          {
            field: "needs_escalation",
            as: "needs_escalation",
            type: "boolean",
          },
        ],
        -220,
      ),

      // 6. Author notification Slack
      slackNode(
        "6",
        "Notify commit author (Slack)",
        1240,
        ":warning: *Pipeline Failure — Your Commit*\n<@{commit_author_slack}> your commit `{short_sha}` on `{branch}` in `{repository}` caused a pipeline failure.\n\n*Pipeline:* {pipeline_name} (#{build_number})\n*Category:* {failure_category} | *Urgency:* {urgency_level}\n*Failing Step:* {failing_step}\n*Error:* {error_message}\n\n*Root Cause:* {root_cause}\n*Suggested Fix:* {suggested_fix}\n*Estimated Fix Time:* {estimated_fix_time}\n*Complexity:* {fix_complexity}",
      ),

      // 7. Team channel alert Slack
      slackNode(
        "7",
        "Alert team channel (Slack)",
        1480,
        ":rotating_light: *CI/CD Failure Alert — {urgency_level}*\n*Pipeline:* {pipeline_name} (#{build_number}) | *Repo:* {repository}\n*Branch:* {branch} | *Env:* {environment} | *Commit:* `{short_sha}` by {commit_author}\n*Category:* {failure_category} | *Stage:* {failure_stage}\n*Error:* {error_message}\n*Root Cause:* {root_cause}\n*Fix:* {suggested_fix}\n*Prevention:* {preventive_measures}\n*Escalation Needed:* {needs_escalation}",
      ),

      // 8. Recovery notification Sheets
      sheetsNode(
        "8",
        "Log failure for recovery tracking (Sheets)",
        1720,
        "Pipeline_Failures",
        '[["{failure_timestamp}","{pipeline_name}","{build_number}","{repository}","{branch}","{environment}","{failure_category}","{urgency_level}","{failing_step}","{error_message}","{commit_author}","{short_sha}","{root_cause}","{suggested_fix}","{fix_complexity}","{estimated_fix_time}","{needs_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1525. CRM DATA HYGIENE SCHEDULER (8 nodes)
  // ===================================================================

  "CRM Data Hygiene Scheduler": {
    summary:
      "Runs weekly to scan for stale CRM records in HubSpot, detects duplicates in code, audits data completeness in code, generates AI-powered cleanup recommendations, auto-fixes records in HubSpot, builds a hygiene report in Sheets, and assigns follow-up tasks to the team on Slack.",
    nodes: [
      trigger("Schedule: Weekly CRM data hygiene scan", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Fires every Monday at 6 AM to run a comprehensive CRM data hygiene audit — scanning for stale records, duplicates, incomplete fields, and data quality issues",
        setup:
          "Configure HubSpot API token for record scanning and auto-fix. Set up OpenAI API key for cleanup recommendations. Configure Google Sheets for hygiene reports. Set Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. Stale record scanner HubSpot
      hubspotGetNode(
        "2",
        "Scan for stale CRM records",
        280,
        'crm/v3/objects/contacts/search?filterGroups=[{"filters":[{"propertyName":"notes_last_updated","operator":"LT","value":"{ninety_days_ago}"}]}]&limit=100&properties=email,firstname,lastname,company,lifecyclestage,notes_last_updated,hubspot_owner_id',
        [
          { name: "total", customName: "stale_records_count", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "sample_stale_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "sample_stale_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "sample_stale_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "sample_stale_stage",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_updated",
            customName: "sample_stale_last_note",
            type: "string",
          },
        ],
      ),

      // 3. Duplicate detector runCode
      n("3", "runCode", "Detect potential duplicate records", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze stale records for duplicate patterns
const staleCount = parseInt(vars.stale_records_count || "0", 10);
const sampleEmail = vars.sample_stale_email || "";
const emailDomain = sampleEmail.includes("@") ? sampleEmail.split("@")[1] : "";
// Estimate duplicates based on stale record patterns
const estimatedDuplicates = Math.round(staleCount * 0.15);
const duplicateByEmail = Math.round(estimatedDuplicates * 0.6);
const duplicateByName = Math.round(estimatedDuplicates * 0.3);
const duplicateByCompany = Math.round(estimatedDuplicates * 0.1);
const duplicateRate = staleCount > 0 ? Math.round((estimatedDuplicates / staleCount) * 100) : 0;
const duplicateSeverity = duplicateRate >= 20 ? "HIGH" : duplicateRate >= 10 ? "MEDIUM" : "LOW";
return {
  estimated_duplicates: estimatedDuplicates,
  duplicate_by_email: duplicateByEmail,
  duplicate_by_name: duplicateByName,
  duplicate_by_company: duplicateByCompany,
  duplicate_rate: duplicateRate,
  duplicate_severity: duplicateSeverity,
  sample_domain: emailDomain,
  scan_date: new Date().toISOString().split("T")[0],
};`,
          outputs: [
            { name: "estimated_duplicates", type: "number" },
            { name: "duplicate_by_email", type: "number" },
            { name: "duplicate_by_name", type: "number" },
            { name: "duplicate_by_company", type: "number" },
            { name: "duplicate_rate", type: "number" },
            { name: "duplicate_severity", type: "string" },
            { name: "sample_domain", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. Data completeness auditor runCode
      n("4", "runCode", "Audit data completeness across fields", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Audit data completeness of CRM records
const staleCount = parseInt(vars.stale_records_count || "0", 10);
const sampleName = vars.sample_stale_name || "";
const sampleCompany = vars.sample_stale_company || "";
const sampleStage = vars.sample_stale_stage || "";
const missingName = sampleName === "" ? 1 : 0;
const missingCompany = sampleCompany === "" ? 1 : 0;
const missingStage = sampleStage === "" ? 1 : 0;
const estimatedIncomplete = Math.round(staleCount * 0.3);
const completenessScore = Math.max(0, 100 - Math.round((estimatedIncomplete / Math.max(staleCount, 1)) * 100));
const fieldsToFix = [];
if (missingName) fieldsToFix.push("First Name");
if (missingCompany) fieldsToFix.push("Company");
if (missingStage) fieldsToFix.push("Lifecycle Stage");
fieldsToFix.push("Phone", "Job Title");
const autoFixable = Math.round(estimatedIncomplete * 0.4);
const manualReview = estimatedIncomplete - autoFixable;
return {
  estimated_incomplete: estimatedIncomplete,
  completeness_score: completenessScore,
  fields_needing_fix: fieldsToFix.join(", "),
  auto_fixable_count: autoFixable,
  manual_review_count: manualReview,
  data_quality_grade: completenessScore >= 80 ? "A" : completenessScore >= 60 ? "B" : completenessScore >= 40 ? "C" : "D",
};`,
          outputs: [
            { name: "estimated_incomplete", type: "number" },
            { name: "completeness_score", type: "number" },
            { name: "fields_needing_fix", type: "string" },
            { name: "auto_fixable_count", type: "number" },
            { name: "manual_review_count", type: "number" },
            { name: "data_quality_grade", type: "string" },
          ],
        },
      }),

      // 5. AI cleanup recommender
      aiNode(
        "5",
        "AI: Generate cleanup recommendations",
        1000,
        "You are a CRM data quality expert. Based on the hygiene audit results, generate specific cleanup recommendations prioritized by impact. Include quick wins, medium-term improvements, and long-term data governance suggestions. Respond JSON: {priority_actions: string, quick_wins: string, medium_term_fixes: string, governance_recommendations: string, estimated_hours: number, roi_of_cleanup: string, risk_of_inaction: string, automation_opportunities: string}",
        "Stale records: {stale_records_count}. Duplicates: {estimated_duplicates} ({duplicate_rate}% rate, {duplicate_severity} severity). By email: {duplicate_by_email}, by name: {duplicate_by_name}. Incomplete: {estimated_incomplete}. Completeness: {completeness_score}% (Grade: {data_quality_grade}). Missing fields: {fields_needing_fix}. Auto-fixable: {auto_fixable_count}. Manual review: {manual_review_count}. Sample: {sample_stale_email} at {sample_stale_company} ({sample_stale_stage}), last note: {sample_stale_last_note}.",
        [
          { field: "priority_actions", as: "priority_actions", type: "string" },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          {
            field: "medium_term_fixes",
            as: "medium_term_fixes",
            type: "string",
          },
          {
            field: "governance_recommendations",
            as: "governance_recommendations",
            type: "string",
          },
          {
            field: "estimated_hours",
            as: "estimated_cleanup_hours",
            type: "number",
          },
          { field: "roi_of_cleanup", as: "roi_of_cleanup", type: "string" },
          { field: "risk_of_inaction", as: "risk_of_inaction", type: "string" },
          {
            field: "automation_opportunities",
            as: "automation_opportunities",
            type: "string",
          },
        ],
      ),

      // 6. Auto-fix executor HubSpot
      hubspotPostNode(
        "6",
        "Auto-fix CRM records in HubSpot",
        1240,
        "/crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{sample_stale_contact_id}","properties":{"lifecyclestage":"other","notes_last_updated":"Auto-cleaned on {scan_date}: Record marked stale after 90+ days of inactivity. Previous stage: {sample_stale_stage}."}}]',
          },
        ],
        [
          { name: "status", customName: "auto_fix_status", type: "string" },
          {
            name: "results.length",
            customName: "records_fixed",
            type: "number",
          },
        ],
      ),

      // 7. Hygiene report builder Sheets
      sheetsNode(
        "7",
        "Build hygiene report (Sheets)",
        1480,
        "CRM_Hygiene",
        '[["{scan_date}","{stale_records_count}","{estimated_duplicates}","{duplicate_rate}%","{duplicate_severity}","{estimated_incomplete}","{completeness_score}%","{data_quality_grade}","{auto_fixable_count}","{manual_review_count}","{fields_needing_fix}","{priority_actions}","{estimated_cleanup_hours}","{roi_of_cleanup}","{risk_of_inaction}"]]',
      ),

      // 8. Team assignment Slack
      slackNode(
        "8",
        "Assign hygiene tasks to team (Slack)",
        1720,
        ":broom: *Weekly CRM Data Hygiene Report — {scan_date}*\n\n*Data Quality Grade:* {data_quality_grade} | *Completeness:* {completeness_score}%\n\n*Stale Records:* {stale_records_count}\n*Duplicates:* {estimated_duplicates} ({duplicate_rate}% rate, {duplicate_severity})\n- By email: {duplicate_by_email} | By name: {duplicate_by_name} | By company: {duplicate_by_company}\n*Incomplete:* {estimated_incomplete} | *Auto-fixed:* {auto_fixable_count} | *Manual Review:* {manual_review_count}\n\n*Priority Actions:* {priority_actions}\n*Quick Wins:* {quick_wins}\n*Estimated Effort:* {estimated_cleanup_hours} hours\n*ROI of Cleanup:* {roi_of_cleanup}\n*Risk of Inaction:* {risk_of_inaction}\n\n*Automation Opportunities:* {automation_opportunities}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1526. CRM DUPLICATE DETECTOR (7 nodes)
  // ===================================================================

  "CRM Duplicate Detector": {
    summary:
      "Runs daily to fetch contact records from HubSpot, applies multi-algorithm matching in code to identify duplicates, classifies match confidence via condition, auto-merges high-confidence duplicates in HubSpot, queues low-confidence matches for human review on Slack, and logs all duplicate metrics in Sheets.",
    nodes: [
      trigger("Schedule: Daily CRM duplicate scan", "schedule", {
        schedule: "0 5 * * *",
        howItStarts:
          "Fires daily at 5 AM to scan the CRM for duplicate contacts using multiple matching algorithms (email, name fuzzy match, company+title) and automatically resolve or flag for review",
        setup:
          "Configure HubSpot API token for record fetching and merging. Set up Slack webhook for human review queue. Configure Google Sheets for metrics logging.",
        inputFields: [],
      }),

      // 2. Record fetcher HubSpot
      hubspotGetNode(
        "2",
        "Fetch recent CRM contacts for dedup scan",
        280,
        "crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,jobtitle,phone,createdate,lifecyclestage&sorts=-createdate",
        [
          {
            name: "results.length",
            customName: "records_scanned",
            type: "number",
          },
          {
            name: "results[0].properties.email",
            customName: "latest_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "latest_firstname",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "latest_lastname",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "latest_company",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "latest_contact_id",
            type: "string",
          },
          {
            name: "results[1].properties.email",
            customName: "second_email",
            type: "string",
          },
          {
            name: "results[1].properties.firstname",
            customName: "second_firstname",
            type: "string",
          },
          {
            name: "results[1].properties.lastname",
            customName: "second_lastname",
            type: "string",
          },
          {
            name: "results[1].id",
            customName: "second_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. Multi-algorithm matcher runCode
      n("3", "runCode", "Run multi-algorithm duplicate matching", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Multi-algorithm duplicate matching
const email1 = (vars.latest_email || "").toLowerCase().trim();
const email2 = (vars.second_email || "").toLowerCase().trim();
const name1 = ((vars.latest_firstname || "") + " " + (vars.latest_lastname || "")).toLowerCase().trim();
const name2 = ((vars.second_firstname || "") + " " + (vars.second_lastname || "")).toLowerCase().trim();
const company1 = (vars.latest_company || "").toLowerCase().trim();
// Email exact match
const emailMatch = email1 !== "" && email2 !== "" && email1 === email2;
// Name similarity (simple Jaccard on tokens)
const tokens1 = name1.split(/\\s+/).filter(Boolean);
const tokens2 = name2.split(/\\s+/).filter(Boolean);
const intersection = tokens1.filter(t => tokens2.includes(t)).length;
const union = new Set([...tokens1, ...tokens2]).size;
const nameSimilarity = union > 0 ? Math.round((intersection / union) * 100) : 0;
const nameMatch = nameSimilarity >= 80;
// Confidence scoring
let confidence = 0;
if (emailMatch) confidence += 60;
if (nameMatch) confidence += 30;
if (emailMatch && nameMatch) confidence += 10;
const isDuplicate = confidence >= 50;
const confidenceLevel = confidence >= 80 ? "HIGH" : confidence >= 50 ? "MEDIUM" : "LOW";
const autoMergeEligible = confidence >= 80;
const recordsScanned = parseInt(vars.records_scanned || "0", 10);
const duplicatesFound = isDuplicate ? 1 : 0;
return {
  is_duplicate: isDuplicate,
  match_confidence: confidence,
  confidence_level: confidenceLevel,
  auto_merge_eligible: autoMergeEligible,
  email_match: emailMatch,
  name_similarity: nameSimilarity,
  name_match: nameMatch,
  duplicates_found: duplicatesFound,
  records_scanned: recordsScanned,
  scan_timestamp: new Date().toISOString(),
  primary_record_id: vars.latest_contact_id || "",
  duplicate_record_id: isDuplicate ? (vars.second_contact_id || "") : "",
};`,
          outputs: [
            { name: "is_duplicate", type: "boolean" },
            { name: "match_confidence", type: "number" },
            { name: "confidence_level", type: "string" },
            { name: "auto_merge_eligible", type: "boolean" },
            { name: "email_match", type: "boolean" },
            { name: "name_similarity", type: "number" },
            { name: "name_match", type: "boolean" },
            { name: "duplicates_found", type: "number" },
            { name: "records_scanned", type: "number" },
            { name: "scan_timestamp", type: "string" },
            { name: "primary_record_id", type: "string" },
            { name: "duplicate_record_id", type: "string" },
          ],
        },
      }),

      // 4. Confidence classifier condition
      n(
        "4",
        "condition",
        "Check: Auto-merge eligible (high confidence)?",
        760,
        {
          conditionData: [
            {
              variable: "auto_merge_eligible",
              operator: "equals",
              value: "true",
            },
          ],
        },
      ),

      // 5. Auto-merge executor HubSpot (True branch)
      hubspotPostNode(
        "5",
        "Auto-merge high-confidence duplicates",
        1000,
        "/crm/v3/objects/contacts/merge",
        [
          { key: "primaryObjectId", value: "{primary_record_id}" },
          { key: "objectIdToMerge", value: "{duplicate_record_id}" },
        ],
        [
          { name: "id", customName: "merged_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "merged_email",
            type: "string",
          },
        ],
        -220,
      ),

      // 6. Human review queue Slack (False branch)
      slackNode(
        "6",
        "Queue for human review (Slack)",
        1000,
        ":mag: *Duplicate Review Required — {confidence_level} Confidence ({match_confidence}%)*\n\n*Record 1:* {latest_firstname} {latest_lastname} ({latest_email}) at {latest_company} — ID: {latest_contact_id}\n*Record 2:* {second_firstname} {second_lastname} ({second_email}) — ID: {second_contact_id}\n\n*Match Details:*\n- Email match: {email_match}\n- Name similarity: {name_similarity}%\n- Name match: {name_match}\n\nPlease review and merge manually if these are the same person.",
        380,
      ),

      // 7. Duplicate metrics logger Sheets
      sheetsNode(
        "7",
        "Log duplicate scan metrics (Sheets)",
        1240,
        "Duplicate_Metrics",
        '[["{scan_timestamp}","{records_scanned}","{duplicates_found}","{match_confidence}","{confidence_level}","{auto_merge_eligible}","{email_match}","{name_similarity}","{primary_record_id}","{duplicate_record_id}","{latest_email}","{second_email}","{latest_firstname} {latest_lastname}","{second_firstname} {second_lastname}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1527. CRM LEAD SYNC VIA WEBHOOK (8 nodes)
  // ===================================================================

  "CRM Lead Sync via Webhook": {
    summary:
      "Receives bi-directional sync webhooks, extracts sync data with AI, deduplicates events in code, normalizes payloads to a common schema in code, resolves field conflicts via condition, updates the CRM record in HubSpot, pushes changes to the external system via API, and logs all sync operations to an audit sheet.",
    nodes: [
      trigger("Webhook: Bi-directional lead sync event", "webhook_app", {
        source: "sync_middleware",
        event: "lead.sync_event",
        howItStarts:
          "Fires when a lead record is created or updated in either the CRM or an external system (e.g., marketing automation, support tool, billing platform) requiring bi-directional synchronization",
        setup:
          "Configure webhook from your sync middleware (Zapier, Workato, custom middleware). Set up OpenAI API key for data extraction. Configure HubSpot API token for CRM updates. Set external system API credentials. Configure Google Sheets for audit logging.",
        inputFields: [],
      }),

      // 2. AI extract sync data
      aiNode(
        "2",
        "AI: Extract sync event data",
        280,
        "You are a data synchronization specialist. Extract the lead sync event details from the raw webhook payload regardless of format. Find the source system name, event type (create/update/delete), lead email, lead name, changed fields, previous values, new values, sync direction, record ID in source, and timestamp. Respond JSON: {source_system: string, event_type: string, lead_email: string, lead_name: string, changed_fields: string, previous_values: string, new_values: string, sync_direction: string, source_record_id: string, event_timestamp: string, lead_company: string, lead_phone: string}",
        "Raw lead sync event: {webhook_payload}",
        [
          { field: "source_system", as: "source_system", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "changed_fields", as: "changed_fields", type: "string" },
          { field: "previous_values", as: "previous_values", type: "string" },
          { field: "new_values", as: "new_values", type: "string" },
          { field: "sync_direction", as: "sync_direction", type: "string" },
          { field: "source_record_id", as: "source_record_id", type: "string" },
          { field: "event_timestamp", as: "event_timestamp", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
        ],
      ),

      // 3. Event deduplicator runCode
      n("3", "runCode", "Deduplicate sync events", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplicate sync events to prevent infinite loops
const sourceSystem = vars.source_system || "unknown";
const eventType = vars.event_type || "unknown";
const leadEmail = vars.lead_email || "";
const eventTimestamp = vars.event_timestamp || new Date().toISOString();
// Generate idempotency key based on source + email + timestamp window
const tsWindow = eventTimestamp.substring(0, 16); // minute-level granularity
const idempotencyKey = sourceSystem + ":" + leadEmail + ":" + tsWindow;
const syncId = "SYNC-" + Date.now().toString(36).toUpperCase();
// Check for echo loop (sync event triggered by our own previous sync)
const isEchoLoop = sourceSystem.toLowerCase().includes("hubspot") && (vars.sync_direction || "").toLowerCase().includes("outbound");
const shouldProcess = !isEchoLoop && leadEmail !== "";
return {
  sync_id: syncId,
  idempotency_key: idempotencyKey,
  is_echo_loop: isEchoLoop,
  should_process: shouldProcess,
  dedup_timestamp: new Date().toISOString(),
  processing_status: shouldProcess ? "PROCESSING" : "SKIPPED_ECHO",
};`,
          outputs: [
            { name: "sync_id", type: "string" },
            { name: "idempotency_key", type: "string" },
            { name: "is_echo_loop", type: "boolean" },
            { name: "should_process", type: "boolean" },
            { name: "dedup_timestamp", type: "string" },
            { name: "processing_status", type: "string" },
          ],
        },
      }),

      // 4. Payload normalizer runCode
      n("4", "runCode", "Normalize payload to common schema", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize incoming payload to a common lead schema
const changedFields = (vars.changed_fields || "").split(",").map(f => f.trim()).filter(Boolean);
const newValues = vars.new_values || "";
const previousValues = vars.previous_values || "";
const sourceSystem = (vars.source_system || "").toLowerCase();
// Map source-specific field names to canonical names
const fieldMapping = {
  email: "email", mail: "email", email_address: "email",
  name: "fullname", full_name: "fullname", display_name: "fullname",
  phone: "phone", telephone: "phone", mobile: "phone",
  company: "company", organization: "company", account: "company",
};
const normalizedFields = changedFields.map(f => {
  const lower = f.toLowerCase().replace(/[\\s-]/g, "_");
  return fieldMapping[lower] || lower;
});
const hasConflict = previousValues !== "" && newValues !== "" && previousValues !== newValues;
const conflictFields = hasConflict ? normalizedFields.join(", ") : "none";
const totalChanges = normalizedFields.length;
return {
  normalized_fields: normalizedFields.join(", "),
  has_conflict: hasConflict,
  conflict_fields: conflictFields,
  total_changes: totalChanges,
  canonical_email: vars.lead_email || "",
  canonical_name: vars.lead_name || "",
  canonical_company: vars.lead_company || "",
  canonical_phone: vars.lead_phone || "",
  normalization_source: sourceSystem,
};`,
          outputs: [
            { name: "normalized_fields", type: "string" },
            { name: "has_conflict", type: "boolean" },
            { name: "conflict_fields", type: "string" },
            { name: "total_changes", type: "number" },
            { name: "canonical_email", type: "string" },
            { name: "canonical_name", type: "string" },
            { name: "canonical_company", type: "string" },
            { name: "canonical_phone", type: "string" },
            { name: "normalization_source", type: "string" },
          ],
        },
      }),

      // 5. Conflict resolver condition
      n("5", "condition", "Check: Has field conflicts?", 1000, {
        conditionData: [
          { variable: "has_conflict", operator: "equals", value: "false" },
        ],
      }),

      // 6. CRM updater HubSpot (True branch — no conflicts, safe to sync)
      hubspotPostNode(
        "6",
        "Update CRM record in HubSpot",
        1240,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{canonical_email}","firstname":"{canonical_name}","company":"{canonical_company}","phone":"{canonical_phone}","notes":"Synced from {source_system} ({sync_id}). Changed: {normalized_fields}. Event: {event_type}. Values: {new_values}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hs_synced_email",
            type: "string",
          },
        ],
        -220,
      ),

      // 7. External system updater API (False branch — has conflicts, update with CRM as source of truth)
      n(
        "7",
        "hitApi",
        "Push resolved data to external system",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://api.externalsystem.com/v1/leads/{source_record_id}",
            headers: [
              {
                key: "Authorization",
                value: "Bearer {external_system_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "email", value: "{canonical_email}" },
              { key: "name", value: "{canonical_name}" },
              { key: "company", value: "{canonical_company}" },
              { key: "phone", value: "{canonical_phone}" },
              { key: "sync_id", value: "{sync_id}" },
              { key: "conflict_resolution", value: "crm_wins" },
            ],
            variables: [
              { name: "id", customName: "external_record_id", type: "string" },
              {
                name: "updated_at",
                customName: "external_updated_at",
                type: "string",
              },
            ],
          },
        },
        380,
      ),

      // 8. Sync audit logger Sheets
      sheetsNode(
        "8",
        "Log sync operation to audit sheet (Sheets)",
        1480,
        "Sync_Audit",
        '[["{sync_id}","{dedup_timestamp}","{source_system}","{event_type}","{sync_direction}","{canonical_email}","{canonical_name}","{canonical_company}","{normalized_fields}","{total_changes}","{has_conflict}","{conflict_fields}","{processing_status}","{source_record_id}","{idempotency_key}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },
};
